import type { Witness } from '../store/booksStore'
import type { AnchoredRow, UnitMapping } from '../types/anchoredRowTypes'
import type { CompleteMicroSegmentationResult, MicroUnit } from '../types/segmentationTypes'
import type { WorkbenchPropagationDiagnostics } from '../api/workbenchPropagationAPI'
import {
  buildManualSegmentationResult,
  extractFoliationSpans,
  extractWordSpans,
  getSegmentableWordData,
  normalizeSplitIndices,
  type WordSpan
} from './manualStudioSegmentationResult'
import { buildUnitReferenceIndexWithRemap } from './manualUnitIdentity'
import { isAutoPropagatedSegmentation, selectPropagationTargets } from './manualStudioPropagationGuards'
import {
  reconcileLedgerSafeBoundaryEdit,
  type LedgerSafeUnassignedFragment,
} from './ledgerSafeBoundaryReconcile'

interface WitnessSplitPlan {
  witness: Witness
  text: string
  segmentableWordSpans: WordSpan[]
  normalizedTokens: string[]
  existingSplitIndices: number[]
  splitIndices: number[]
}

export interface PropagationTuning {
  anchorSearchWindow: number
  anchorMinComparisons: number
  anchorMinScore: number
  anchorDistancePenalty: number
  anchorBoundaryWeight: number
  anchorGapPenalty: number
  rebalanceSearchWindow: number
  rebalancePasses: number
  rebalanceRatioWeight: number
  rebalanceAnchorWeight: number
  rebalanceMovePenalty: number
  rebalanceSegmentLongFactor: number
  rebalanceSegmentShortFactor: number
  rebalanceMinSegmentWords: number
}

export interface WorkbenchPropagationPlan {
  witness: Witness
  splitIndices: number[]
  segmentCount: number
  protected: boolean
  rowAssignments?: WorkbenchPropagationRowAssignment[]
  exemplarWitnessIds?: string[]
  strategy?: 'single-source' | 'ledger-conditioned' | 'translation-aligned' | 'ledger-span-aligned'
  translationProgress?: {
    completedBatches: number
    totalBatches: number
    complete: boolean
  }
  confidence?: string
  notes?: string
  diagnostics?: WorkbenchPropagationDiagnostics
}

export interface WorkbenchPropagationPreview {
  sourceWitness: Witness
  sourceSplitIndices: number[]
  segmentCount: number
  plans: WorkbenchPropagationPlan[]
  protectedWitnessIds: Set<string>
  mode?: 'proposal' | 'applied-audit'
}

export interface WorkbenchPropagationRowAssignment {
  rowId: string
  segmentIndex: number | null
  startTokenIndex?: number
  endTokenIndex?: number
  score?: number
  translationStatus?: 'mapped' | 'gap' | 'uncertain' | 'transposed'
}

export interface WorkbenchPropagationApplication {
  witnessUpdates: Array<{
    witnessId: string
    type: 'segmentation'
    result: CompleteMicroSegmentationResult
    status?: Witness['processing_status']
  }>
  rowUpdates: AnchoredRow[]
  changedRowIds: string[]
  unassignedFragmentUnitUuids?: string[]
  outOfScopeAssignmentRowIds?: string[]
  outsideLocalContextAssignmentRowIds?: string[]
  acceptedAssignmentRowIds?: string[]
}

export interface SelectedUnitPropagationRepairPreview {
  targetWitness: Witness
  selectedRowIds: string[]
  contextRowIds: string[]
  anchorRowIds: string[]
  previouslyAppliedRowIds: string[]
  baseRowFingerprints: Record<string, string>
  selectedRepairTokenInterval?: {
    startTokenIndex: number
    endTokenIndex: number
  }
  splitIndices: number[]
  rowAssignments: WorkbenchPropagationRowAssignment[]
  segmentCount: number
  notes?: string
  diagnostics?: WorkbenchPropagationDiagnostics
}

export interface WorkbenchPropagationApplyOptions {
  selectedWitnessIds?: string[]
}

export interface WorkbenchPropagationPreviewOptions {
  allowExistingSegmentationWitnessIds?: string[]
}

export interface WitnessScopedPropagationProposal {
  targetWitness: Witness
  referenceWitness: Witness
  splitIndices: number[]
  rowAssignments?: WorkbenchPropagationRowAssignment[]
}

export interface AppliedWitnessCoverageDuplicate {
  unitUuid: string
  rowIds: string[]
}

export interface AppliedWitnessCoverageUnclaimedUnit {
  unitUuid: string
  unitLabel?: string
  text: string
}

export interface AppliedWitnessCoverageDiagnostics {
  hasSegmentationResult: boolean
  totalRows: number
  totalUnits: number
  mappedRowIds: string[]
  deliberateGapRowIds: string[]
  unmappedRowIds: string[]
  staleMappingRowIds: string[]
  duplicateUnitMappings: AppliedWitnessCoverageDuplicate[]
  unclaimedUnits: AppliedWitnessCoverageUnclaimedUnit[]
}

export interface AppliedWitnessAuditPlan {
  splitIndices: number[]
  segmentCount: number
  rowAssignments: WorkbenchPropagationRowAssignment[]
  diagnostics: AppliedWitnessCoverageDiagnostics
  notes?: string
}

const ANCHOR_CONTEXT_RADIUS = 8
const ANCHOR_SEARCH_WINDOW = 140
const ANCHOR_MIN_COMPARISONS = 4
const ANCHOR_MIN_SCORE = 1.4
const ANCHOR_DISTANCE_PENALTY = 0.035
const ANCHOR_BOUNDARY_WEIGHT = 0.6
const ANCHOR_GAP_PENALTY = 0.02
const REBALANCE_SEARCH_WINDOW = 72
const REBALANCE_PASSES = 2
const REBALANCE_RATIO_WEIGHT = 5.8
const REBALANCE_ANCHOR_WEIGHT = 1.35
const REBALANCE_MOVE_PENALTY = 0.018
const REBALANCE_SEGMENT_LONG_FACTOR = 2.6
const REBALANCE_SEGMENT_SHORT_FACTOR = 0.42
const REBALANCE_MIN_SEGMENT_WORDS = 2
const STABLE_BOUNDARY_MAX_WORD_DRIFT = 42
const GLOBAL_EXEMPLAR_POOL_SIZE = 4
const SELECTED_REPAIR_ASSIGNMENT_CLUSTER_MAX_GAP = 3

export const DEFAULT_PROPAGATION_TUNING: PropagationTuning = {
  anchorSearchWindow: ANCHOR_SEARCH_WINDOW,
  anchorMinComparisons: ANCHOR_MIN_COMPARISONS,
  anchorMinScore: ANCHOR_MIN_SCORE,
  anchorDistancePenalty: ANCHOR_DISTANCE_PENALTY,
  anchorBoundaryWeight: ANCHOR_BOUNDARY_WEIGHT,
  anchorGapPenalty: ANCHOR_GAP_PENALTY,
  rebalanceSearchWindow: REBALANCE_SEARCH_WINDOW,
  rebalancePasses: REBALANCE_PASSES,
  rebalanceRatioWeight: REBALANCE_RATIO_WEIGHT,
  rebalanceAnchorWeight: REBALANCE_ANCHOR_WEIGHT,
  rebalanceMovePenalty: REBALANCE_MOVE_PENALTY,
  rebalanceSegmentLongFactor: REBALANCE_SEGMENT_LONG_FACTOR,
  rebalanceSegmentShortFactor: REBALANCE_SEGMENT_SHORT_FACTOR,
  rebalanceMinSegmentWords: REBALANCE_MIN_SEGMENT_WORDS
}

export const summarizeAppliedWitnessCoverageDiagnostics = (
  diagnostics: AppliedWitnessCoverageDiagnostics,
): string => {
  if (!diagnostics.hasSegmentationResult) {
    return 'Applied-state audit: no current segmentation result to inspect.'
  }

  const parts: string[] = [`${diagnostics.mappedRowIds.length} mapped row${diagnostics.mappedRowIds.length === 1 ? '' : 's'}`]
  if (diagnostics.deliberateGapRowIds.length > 0) {
    parts.push(`${diagnostics.deliberateGapRowIds.length} deliberate gap${diagnostics.deliberateGapRowIds.length === 1 ? '' : 's'}`)
  }
  if (diagnostics.unmappedRowIds.length > 0) {
    parts.push(`${diagnostics.unmappedRowIds.length} unmapped row${diagnostics.unmappedRowIds.length === 1 ? '' : 's'}`)
  }
  if (diagnostics.staleMappingRowIds.length > 0) {
    parts.push(`${diagnostics.staleMappingRowIds.length} stale mapping${diagnostics.staleMappingRowIds.length === 1 ? '' : 's'}`)
  }
  if (diagnostics.unclaimedUnits.length > 0) {
    parts.push(`${diagnostics.unclaimedUnits.length} unclaimed unit${diagnostics.unclaimedUnits.length === 1 ? '' : 's'}`)
  }
  if (diagnostics.duplicateUnitMappings.length > 0) {
    parts.push(`${diagnostics.duplicateUnitMappings.length} duplicated unit mapping${diagnostics.duplicateUnitMappings.length === 1 ? '' : 's'}`)
  }
  return `Applied-state audit: ${parts.join(' · ')}`
}

const findNearestSplitIndex = (targetCharOffset: number, wordSpans: WordSpan[]): number | null => {
  // mutation-ignore-next-line equivalent: with zero or one word there is no legal split; the loop below also returns null.
  if (wordSpans.length <= 1) {
    return null
  }

  let bestIndex = -1
  let bestDistance = Number.POSITIVE_INFINITY
  for (let idx = 1; idx < wordSpans.length; idx += 1) {
    const distance = Math.abs(wordSpans[idx].start - targetCharOffset)
    if (distance < bestDistance) {
      bestDistance = distance
      bestIndex = idx
    }
  }

  // mutation-ignore-next-line equivalent: bestIndex is either -1 or a legal split index >= 1 because the loop starts at 1.
  return bestIndex >= 0 ? bestIndex : null
}

export const deriveSplitIndicesFromSegmentation = (
  segmentationResult: CompleteMicroSegmentationResult | undefined,
  wordSpans: WordSpan[]
): number[] => {
  if (!segmentationResult?.chunk_results?.length) {
    return []
  }

  // mutation-ignore-next-line equivalent: normalizeSplitIndices rejects every split when only one word is segmentable.
  if (wordSpans.length <= 1) {
    return []
  }

  const metadata = segmentationResult.segmentation_metadata
  // mutation-ignore-next-line equivalent: non-object metadata still yields no usable manual split array.
  const manualSplitsRaw = metadata && typeof metadata === 'object'
    ? (metadata as Record<string, unknown>).manual_split_indices
    : null
  if (Array.isArray(manualSplitsRaw)) {
    const manualSplits = manualSplitsRaw
      .map(value => Number(value))
      .filter(value => Number.isFinite(value))
    if (manualSplits.length > 0) {
      return normalizeSplitIndices(manualSplits, wordSpans.length)
    }
  }

  const starts = segmentationResult.chunk_results
    .flatMap(chunk => (chunk.micro_units || []).map(unit => unit.start_position))
    .filter(start => Number.isFinite(start))
    .sort((left, right) => left - right)

  // mutation-ignore-next-line equivalent: one start position cannot define an internal boundary.
  if (starts.length <= 1) {
    return []
  }

  const initialStart = starts[0]
  const splitIndices: number[] = []
  starts.forEach(start => {
    // mutation-ignore-next-line equivalent: the first start maps to the first possible split and is removed by normalization.
    if (start <= initialStart) {
      return
    }
    const nearestIndex = findNearestSplitIndex(start, wordSpans)
    if (nearestIndex !== null) {
      splitIndices.push(nearestIndex)
    }
  })

  return normalizeSplitIndices(splitIndices, wordSpans.length)
}

const mapSplitIndicesByProportion = (
  groundTruthSplitIndices: number[],
  groundTruthWordSpans: WordSpan[],
  groundTruthLength: number,
  targetWordSpans: WordSpan[],
  targetLength: number
): number[] => {
  if (groundTruthSplitIndices.length === 0) {
    return []
  }
  // mutation-ignore-next-line equivalent: fewer than two words on either side cannot produce an internal mapped split.
  if (groundTruthWordSpans.length <= 1) {
    return []
  }
  // mutation-ignore-next-line equivalent: fewer than two target words cannot produce an internal mapped split.
  if (targetWordSpans.length <= 1) {
    return []
  }

  const mapped: number[] = []
  const safeGroundTruthLength = Math.max(groundTruthLength, 1)

  groundTruthSplitIndices.forEach(splitIndex => {
    const splitSpan = groundTruthWordSpans[splitIndex]
    if (!splitSpan) {
      return
    }
    const ratio = splitSpan.start / safeGroundTruthLength
    const targetOffset = Math.max(0, Math.min(targetLength, Math.round(ratio * targetLength)))
    const nearestIndex = findNearestSplitIndex(targetOffset, targetWordSpans)
    if (nearestIndex !== null) {
      mapped.push(nearestIndex)
    }
  })

  return normalizeSplitIndices(mapped, targetWordSpans.length)
}

const normalizePropagationToken = (token: string): string => {
  if (!token) {
    return ''
  }
  return token
    .normalize('NFKD')
    .replace(/[\u064B-\u065F\u0670]/g, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/[^\u0600-\u06FFA-Za-z0-9]+/g, '')
    .toLowerCase()
}

const looseTokenSimilarity = (leftToken: string, rightToken: string): number => {
  // mutation-ignore-next-line equivalent: a one-sided empty token also falls through to the min-length guard and returns zero.
  if (!leftToken || !rightToken) {
    return 0
  }
  if (leftToken === rightToken) {
    return 1
  }
  const minLength = Math.min(leftToken.length, rightToken.length)
  if (minLength < 4) {
    return 0
  }
  if (leftToken.includes(rightToken) || rightToken.includes(leftToken)) {
    return 0.55
  }
  return 0
}

const getTokenOrEmpty = (tokens: string[], index: number): string => tokens[index] ?? ''

const getAnchorScoreBoost = (compared: number, score: number, tuning: PropagationTuning): number => {
  if (compared < tuning.anchorMinComparisons) {
    return 0
  }
  if (score < tuning.anchorMinScore) {
    return 0
  }
  return 0.35
}

const scoreSplitAnchorAlignment = (
  groundTruthTokens: string[],
  targetTokens: string[],
  groundTruthSplitIndex: number,
  targetSplitIndex: number
): { score: number; compared: number } => {
  let score = 0
  let compared = 0

  for (let offset = 1; offset <= ANCHOR_CONTEXT_RADIUS; offset += 1) {
    const gtIndex = groundTruthSplitIndex - offset
    const targetIndex = targetSplitIndex - offset
    // mutation-ignore-next-line equivalent: when either side is before the text, later offsets only move farther out.
    if (gtIndex < 0 || targetIndex < 0) {
      break
    }

    const leftToken = groundTruthTokens[gtIndex]
    const rightToken = targetTokens[targetIndex]
    if (!leftToken || !rightToken) {
      continue
    }
    compared += 1
    const similarity = looseTokenSimilarity(leftToken, rightToken)
    const weight = 1 + (ANCHOR_CONTEXT_RADIUS - offset) * 0.08
    score += similarity * weight
  }

  for (let offset = 0; offset < ANCHOR_CONTEXT_RADIUS; offset += 1) {
    const gtIndex = groundTruthSplitIndex + offset
    const targetIndex = targetSplitIndex + offset
    // mutation-ignore-next-line equivalent: one-sided overflow cannot contribute comparable tokens on later offsets.
    if (gtIndex >= groundTruthTokens.length && targetIndex >= targetTokens.length) {
      break
    }

    const leftToken = groundTruthTokens[gtIndex]
    const rightToken = targetTokens[targetIndex]
    if (!leftToken || !rightToken) {
      continue
    }
    compared += 1
    const similarity = looseTokenSimilarity(leftToken, rightToken)
    const weight = 1 + (ANCHOR_CONTEXT_RADIUS - offset - 1) * 0.08
    score += similarity * weight
  }

  return { score, compared }
}

const mapSplitIndicesByAnchors = (
  groundTruthSplitIndices: number[],
  groundTruthWordSpans: WordSpan[],
  groundTruthLength: number,
  targetWordSpans: WordSpan[],
  targetLength: number,
  tuning: PropagationTuning
): number[] => {
  if (groundTruthSplitIndices.length === 0) {
    return []
  }
  // mutation-ignore-next-line equivalent: fewer than two source words cannot anchor an internal split.
  if (groundTruthWordSpans.length <= 1) {
    return []
  }
  // mutation-ignore-next-line equivalent: fewer than two target words cannot anchor an internal split.
  if (targetWordSpans.length <= 1) {
    return []
  }

  if (groundTruthSplitIndices.length > targetWordSpans.length - 1) {
    return mapSplitIndicesByProportion(
      groundTruthSplitIndices,
      groundTruthWordSpans,
      groundTruthLength,
      targetWordSpans,
      targetLength
    )
  }

  const groundTruthTokens = groundTruthWordSpans.map(span => normalizePropagationToken(span.word))
  const targetTokens = targetWordSpans.map(span => normalizePropagationToken(span.word))
  const safeGroundTruthLength = Math.max(groundTruthLength, 1)
  const splitCount = groundTruthSplitIndices.length
  const targetSplitMax = targetWordSpans.length - 1
  const expectedSplits: number[] = []

  for (let splitOrder = 0; splitOrder < splitCount; splitOrder += 1) {
    const splitIndex = groundTruthSplitIndices[splitOrder]
    const splitSpan = groundTruthWordSpans[splitIndex]
    if (!splitSpan) {
      return mapSplitIndicesByProportion(
        groundTruthSplitIndices,
        groundTruthWordSpans,
        groundTruthLength,
        targetWordSpans,
        targetLength
      )
    }

    const ratio = splitSpan.start / safeGroundTruthLength
    const targetOffset = Math.max(0, Math.min(targetLength, Math.round(ratio * targetLength)))
    const proportionalIndex = findNearestSplitIndex(targetOffset, targetWordSpans)
    const minSplit = splitOrder + 1
    const maxSplit = targetSplitMax - (splitCount - splitOrder - 1)
    const fallbackSplit = Math.max(minSplit, Math.min(maxSplit, minSplit))
    expectedSplits.push(
      Math.max(minSplit, Math.min(maxSplit, proportionalIndex ?? fallbackSplit))
    )
  }

  type SplitCandidate = { index: number; localScore: number }
  const candidatesBySplit: SplitCandidate[][] = []

  for (let splitOrder = 0; splitOrder < splitCount; splitOrder += 1) {
    const splitIndex = groundTruthSplitIndices[splitOrder]
    const minSplit = splitOrder + 1
    const maxSplit = targetSplitMax - (splitCount - splitOrder - 1)
    const expectedSplit = expectedSplits[splitOrder]

    let searchStart = Math.max(minSplit, expectedSplit - tuning.anchorSearchWindow)
    let searchEnd = Math.min(maxSplit, expectedSplit + tuning.anchorSearchWindow)
    if (searchStart > searchEnd) {
      searchStart = minSplit
      searchEnd = maxSplit
    }

    const splitCandidates: SplitCandidate[] = []
    for (let candidate = searchStart; candidate <= searchEnd; candidate += 1) {
      const { score, compared } = scoreSplitAnchorAlignment(
        groundTruthTokens,
        targetTokens,
        splitIndex,
        candidate
      )
      const leftBoundary = looseTokenSimilarity(
        getTokenOrEmpty(groundTruthTokens, splitIndex - 1),
        getTokenOrEmpty(targetTokens, candidate - 1)
      )
      const rightBoundary = looseTokenSimilarity(
        getTokenOrEmpty(groundTruthTokens, splitIndex),
        getTokenOrEmpty(targetTokens, candidate)
      )
      const boundaryScore = (leftBoundary + rightBoundary) * tuning.anchorBoundaryWeight
      const anchorScoreBoost = getAnchorScoreBoost(compared, score, tuning)
      const localScore =
        score +
        boundaryScore +
        anchorScoreBoost -
        Math.abs(candidate - expectedSplit) * tuning.anchorDistancePenalty

      splitCandidates.push({
        index: candidate,
        localScore
      })
    }

    // mutation-ignore-next-line unreachable: search bounds are corrected above before candidates are generated.
    if (splitCandidates.length === 0) {
      return mapSplitIndicesByProportion(
        groundTruthSplitIndices,
        groundTruthWordSpans,
        groundTruthLength,
        targetWordSpans,
        targetLength
      )
    }

    candidatesBySplit.push(splitCandidates)
  }

  const dp: number[][] = candidatesBySplit.map(candidates => new Array(candidates.length).fill(Number.NEGATIVE_INFINITY))
  const prevChoice: number[][] = candidatesBySplit.map(candidates => new Array(candidates.length).fill(-1))

  candidatesBySplit[0].forEach((candidate, candidateIdx) => {
    dp[0][candidateIdx] = candidate.localScore
  })

  for (let splitOrder = 1; splitOrder < splitCount; splitOrder += 1) {
    const previousCandidates = candidatesBySplit[splitOrder - 1]
    const currentCandidates = candidatesBySplit[splitOrder]
    const expectedGap = Math.max(1, expectedSplits[splitOrder] - expectedSplits[splitOrder - 1])

    for (let currentIdx = 0; currentIdx < currentCandidates.length; currentIdx += 1) {
      const currentCandidate = currentCandidates[currentIdx]
      let bestScore = Number.NEGATIVE_INFINITY
      let bestPreviousIdx = -1

      for (let previousIdx = 0; previousIdx < previousCandidates.length; previousIdx += 1) {
        const previousScore = dp[splitOrder - 1][previousIdx]
        if (!Number.isFinite(previousScore)) {
          continue
        }

        const previousCandidate = previousCandidates[previousIdx]
        // mutation-ignore-next-line equivalent: non-monotone choices are also rejected by the final finite-score guard.
        if (currentCandidate.index <= previousCandidate.index) {
          continue
        }

        const actualGap = currentCandidate.index - previousCandidate.index
        const gapPenalty = Math.abs(actualGap - expectedGap) * tuning.anchorGapPenalty
        const candidateScore = previousScore + currentCandidate.localScore - gapPenalty
        if (candidateScore > bestScore) {
          bestScore = candidateScore
          bestPreviousIdx = previousIdx
        }
      }

      dp[splitOrder][currentIdx] = bestScore
      prevChoice[splitOrder][currentIdx] = bestPreviousIdx
    }
  }

  let bestFinalIdx = -1
  let bestFinalScore = Number.NEGATIVE_INFINITY
  const finalRow = dp[splitCount - 1]
  for (let idx = 0; idx < finalRow.length; idx += 1) {
    if (finalRow[idx] > bestFinalScore) {
      bestFinalScore = finalRow[idx]
      bestFinalIdx = idx
    }
  }

  // mutation-ignore-next-line equivalent: candidates are always finite when a monotone path exists; otherwise proportional fallback is used.
  if (bestFinalIdx < 0 || !Number.isFinite(bestFinalScore)) {
    return mapSplitIndicesByProportion(
      groundTruthSplitIndices,
      groundTruthWordSpans,
      groundTruthLength,
      targetWordSpans,
      targetLength
    )
  }

  const chosenSplits = new Array<number>(splitCount)
  let splitOrder = splitCount - 1
  let chosenCandidateIdx = bestFinalIdx
  while (splitOrder >= 0) {
    const candidate = candidatesBySplit[splitOrder][chosenCandidateIdx]
    if (!candidate) {
      return mapSplitIndicesByProportion(
        groundTruthSplitIndices,
        groundTruthWordSpans,
        groundTruthLength,
        targetWordSpans,
        targetLength
      )
    }
    chosenSplits[splitOrder] = candidate.index
    chosenCandidateIdx = prevChoice[splitOrder][chosenCandidateIdx]
    splitOrder -= 1
  }

  return normalizeSplitIndices(chosenSplits, targetWordSpans.length)
}

const medianNumber = (values: number[]): number => {
  if (values.length === 0) {
    return Number.NaN
  }
  const sorted = [...values].sort((left, right) => left - right)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 1 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2
}

interface PropagationRowProfile {
  rowId: string
  position: number
  readings: string[][]
  minLength: number
  maxLength: number
  medianLength: number
}

interface PropagationRowReading {
  witnessId: string
  tokens: string[]
}

interface PropagationTokenCache {
  get: (witnessId: string, text: string) => string[]
}

const tokenizePropagationText = (text: string): string[] => {
  if (!text.trim()) {
    return []
  }

  const wordSpans = extractWordSpans(text)
  const foliationSpans = extractFoliationSpans(text, wordSpans)
  return getSegmentableWordData(wordSpans, foliationSpans).segmentableWordSpans
    .map(span => normalizePropagationToken(span.word))
    .filter(Boolean)
}

const createPropagationTokenCache = (): PropagationTokenCache => {
  const cache = new Map<string, string[]>()
  return {
    get: (witnessId: string, text: string) => {
      const cacheKey = `${witnessId}::${text}`
      const existing = cache.get(cacheKey)
      if (existing) {
        return existing
      }
      const tokens = tokenizePropagationText(text)
      cache.set(cacheKey, tokens)
      return tokens
    }
  }
}

const buildWitnessSplitPlan = (witness: Witness): WitnessSplitPlan => {
  const text = witness.uploaded_file?.content || ''
  const wordSpans = extractWordSpans(text)
  const foliationSpans = extractFoliationSpans(text, wordSpans)
  const segmentableWordSpans = getSegmentableWordData(wordSpans, foliationSpans).segmentableWordSpans
  const existingSplitIndices = deriveSplitIndicesFromSegmentation(witness.segmentation_result, segmentableWordSpans)

  return {
    witness,
    text,
    segmentableWordSpans,
    normalizedTokens: segmentableWordSpans.map(span => normalizePropagationToken(span.word)).filter(Boolean),
    existingSplitIndices,
    splitIndices: existingSplitIndices
  }
}

const getProtectedWitnessIdsFromRows = (rows: AnchoredRow[], sourceWitnessId: string): Set<string> => {
  const protectedWitnessIds = new Set<string>()
  rows.forEach(row => {
    row.unitMappings.forEach(mapping => {
      if (!mapping.excluded && mapping.witnessId && mapping.witnessId !== sourceWitnessId) {
        protectedWitnessIds.add(mapping.witnessId)
      }
    })
  })
  return protectedWitnessIds
}

const buildPropagationRowProfiles = ({
  rows,
  preferredWitnessIds,
  tokenCache
}: {
  rows: AnchoredRow[]
  preferredWitnessIds?: string[]
  tokenCache?: PropagationTokenCache
}): PropagationRowProfile[] =>
  sortRows(rows)
    .map(row => {
      const availableReadings: PropagationRowReading[] = row.unitMappings
        .filter(mapping => !mapping.excluded)
        .map(mapping => ({
          witnessId: mapping.witnessId,
          tokens: tokenCache
            ? tokenCache.get(mapping.witnessId, mapping.textSnapshot || '')
            : tokenizePropagationText(mapping.textSnapshot || '')
        }))
        .filter(reading => reading.tokens.length > 0)

      if (availableReadings.length === 0) {
        return null
      }

      const preferredSet = new Set(preferredWitnessIds || [])
      const selectedReadings = preferredSet.size === 0
        ? availableReadings
        : availableReadings.filter(reading => preferredSet.has(reading.witnessId))

      if (selectedReadings.length === 0) {
        return null
      }

      const readings = selectedReadings.map(reading => reading.tokens)

      const lengths = readings.map(tokens => tokens.length)
      const medianLength = medianNumber(lengths)

      return {
        rowId: row.id,
        position: row.position,
        readings,
        minLength: Math.min(...lengths),
        maxLength: Math.max(...lengths),
        medianLength: Number.isFinite(medianLength) ? medianLength : lengths[0]
      }
    })
    .filter((profile): profile is PropagationRowProfile => Boolean(profile))

const computeWitnessTokenSimilarity = (leftTokens: string[], rightTokens: string[]): number => {
  // mutation-ignore-next-line equivalent: empty inputs also produce zero through overlap/LCS/boundary scoring.
  if (leftTokens.length === 0 || rightTokens.length === 0) {
    return 0
  }

  const overlap = countTokenOverlap(leftTokens, rightTokens)
  const lcsLength = computeTokenLcsLength(leftTokens, rightTokens)
  const leftBoundaryBonus = leftTokens[0] === rightTokens[0] ? 0.6 : 0
  const rightBoundaryBonus = leftTokens[leftTokens.length - 1] === rightTokens[rightTokens.length - 1] ? 0.6 : 0

  return (
    (overlap / Math.max(leftTokens.length, rightTokens.length)) * 6 +
    (lcsLength / Math.max(leftTokens.length, rightTokens.length)) * 8 +
    leftBoundaryBonus +
    rightBoundaryBonus
  )
}

const rankPropagationExemplarWitnessIds = ({
  targetWitness,
  candidateWitnesses,
  sourceWitnessId,
  tokenCache
}: {
  targetWitness: Witness
  candidateWitnesses: Witness[]
  sourceWitnessId: string
  tokenCache: PropagationTokenCache
}): string[] => {
  const targetTokens = tokenCache.get(targetWitness.id, targetWitness.uploaded_file?.content || '')
  if (targetTokens.length === 0) {
    return []
  }

  return candidateWitnesses
    .filter(witness => witness.id !== targetWitness.id && witness.id !== sourceWitnessId)
    .map(witness => ({
      witnessId: witness.id,
      score: computeWitnessTokenSimilarity(
        tokenCache.get(witness.id, witness.uploaded_file?.content || ''),
        targetTokens
      )
    }))
    .filter(entry => entry.score > 0)
    .sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score
      }
      return left.witnessId.localeCompare(right.witnessId)
    })
    .slice(0, GLOBAL_EXEMPLAR_POOL_SIZE)
    .map(entry => entry.witnessId)
}

const countTokenOverlap = (leftTokens: string[], rightTokens: string[]): number => {
  // mutation-ignore-next-line equivalent: either empty input leaves the overlap counter at zero.
  if (leftTokens.length === 0 || rightTokens.length === 0) {
    return 0
  }

  const counts = new Map<string, number>()
  leftTokens.forEach(token => {
    counts.set(token, (counts.get(token) || 0) + 1)
  })

  let overlap = 0
  rightTokens.forEach(token => {
    const available = counts.get(token) || 0
    if (available > 0) {
      overlap += 1
      counts.set(token, available - 1)
    }
  })

  return overlap
}

const computeTokenLcsLength = (leftTokens: string[], rightTokens: string[]): number => {
  // mutation-ignore-next-line equivalent: the dynamic-programming table also returns zero for empty input.
  if (leftTokens.length === 0 || rightTokens.length === 0) {
    return 0
  }

  const previous = new Array(rightTokens.length + 1).fill(0)
  const current = new Array(rightTokens.length + 1).fill(0)

  for (let leftIndex = 1; leftIndex <= leftTokens.length; leftIndex += 1) {
    current.fill(0)
    for (let rightIndex = 1; rightIndex <= rightTokens.length; rightIndex += 1) {
      if (leftTokens[leftIndex - 1] === rightTokens[rightIndex - 1]) {
        current[rightIndex] = previous[rightIndex - 1] + 1
      } else {
        current[rightIndex] = Math.max(previous[rightIndex], current[rightIndex - 1])
      }
    }
    for (let index = 0; index < current.length; index += 1) {
      previous[index] = current[index]
    }
  }

  return previous[rightTokens.length] || 0
}

const scoreRowExemplar = (readingTokens: string[], spanTokens: string[]): number => {
  if (readingTokens.length === 0 || spanTokens.length === 0) {
    return Number.NEGATIVE_INFINITY
  }

  const exactJoinMatch = readingTokens.join(' ') === spanTokens.join(' ')
  if (exactJoinMatch) {
    return 18 + readingTokens.length * 3
  }

  const lcsLength = computeTokenLcsLength(readingTokens, spanTokens)
  const overlap = countTokenOverlap(readingTokens, spanTokens)
  const positionMatches = readingTokens.reduce((count, token, index) => (
    token === spanTokens[index] ? count + 1 : count
  ), 0)
  const firstTokenBonus = readingTokens[0] === spanTokens[0] ? 1.2 : 0
  const lastTokenBonus = readingTokens[readingTokens.length - 1] === spanTokens[spanTokens.length - 1] ? 1.2 : 0

  return (
    (lcsLength / Math.max(readingTokens.length, spanTokens.length)) * 10 +
    (overlap / Math.max(readingTokens.length, spanTokens.length)) * 6 +
    positionMatches * 1.4 +
    firstTokenBonus +
    lastTokenBonus -
    Math.abs(readingTokens.length - spanTokens.length) * 1.2
  )
}

const scoreRowSpan = ({
  profile,
  spanTokens,
  startIndex,
  totalTokenCount,
  expectedStart
}: {
  profile: PropagationRowProfile
  spanTokens: string[]
  startIndex: number
  totalTokenCount: number
  expectedStart: number
}): number => {
  if (spanTokens.length === 0) {
    return Number.NEGATIVE_INFINITY
  }

  const exemplarScore = profile.readings.reduce(
    (bestScore, readingTokens) => Math.max(bestScore, scoreRowExemplar(readingTokens, spanTokens)),
    Number.NEGATIVE_INFINITY
  )

  const startPenalty = Math.abs(startIndex - expectedStart) * 0.28
  const medianLengthPenalty = Math.abs(spanTokens.length - profile.medianLength) * 0.9
  const longSpanPenalty = spanTokens.length > profile.maxLength + 2
    ? (spanTokens.length - (profile.maxLength + 2)) * 1.35
    : 0
  const shortSpanPenalty = spanTokens.length < Math.max(1, profile.minLength - 1)
    ? (Math.max(1, profile.minLength - 1) - spanTokens.length) * 1.35
    : 0
  const tailPenalty = totalTokenCount > 0 && profile.position > 0
    ? Math.max(0, (startIndex / totalTokenCount) - ((expectedStart + profile.medianLength) / totalTokenCount + 0.35)) * 6
    : 0

  return exemplarScore - startPenalty - medianLengthPenalty - longSpanPenalty - shortSpanPenalty - tailPenalty
}

const derivePlanSegments = (tokens: string[], splitIndices: number[]): string[][] => {
  const normalizedSplits = normalizeSplitIndices(splitIndices, tokens.length)
  const segments: string[][] = []
  let startIndex = 0
  normalizedSplits.forEach(splitIndex => {
    segments.push(tokens.slice(startIndex, splitIndex))
    startIndex = splitIndex
  })
  segments.push(tokens.slice(startIndex))
  return segments.filter(segment => segment.length > 0)
}

const getRowSkipPenalty = (profile: PropagationRowProfile): number => (
  2.2 +
  Math.min(12, profile.medianLength) * 0.75 +
  Math.max(0, profile.readings.length - 1) * 0.35
)

const getSegmentSkipPenalty = (segment: string[]): number => (
  4.6 + Math.min(16, segment.length) * 0.9
)

const alignSegmentsToRows = (
  rowProfiles: PropagationRowProfile[],
  segments: string[][]
): { score: number; assignments: WorkbenchPropagationRowAssignment[] } => {
  const rowCount = rowProfiles.length
  const segmentCount = segments.length

  if (rowCount === 0) {
    return { score: -segmentCount * 8, assignments: [] }
  }
  if (segmentCount === 0) {
    return {
      score: rowProfiles.reduce((total, profile) => total - getRowSkipPenalty(profile), 0),
      assignments: rowProfiles.map(profile => ({ rowId: profile.rowId, segmentIndex: null }))
    }
  }

  const matchScores = rowProfiles.map(profile => segments.map(segmentTokens => {
    const bestReadingScore = profile.readings.reduce(
      (best, reading) => Math.max(best, scoreRowExemplar(reading, segmentTokens)),
      Number.NEGATIVE_INFINITY
    )
    return Number.isFinite(bestReadingScore) ? bestReadingScore : -8
  }))

  const scores = Array.from({ length: rowCount + 1 }, () => new Array(segmentCount + 1).fill(Number.NEGATIVE_INFINITY))
  const steps = Array.from({ length: rowCount + 1 }, () => new Array<'match' | 'skip-row' | 'skip-segment' | null>(segmentCount + 1).fill(null))

  scores[0][0] = 0

  for (let rowIndex = 1; rowIndex < rowCount + 1; rowIndex += 1) {
    scores[rowIndex][0] = scores[rowIndex - 1][0] - getRowSkipPenalty(rowProfiles[rowIndex - 1] as PropagationRowProfile)
    steps[rowIndex][0] = 'skip-row'
  }

  for (let segmentIndex = 1; segmentIndex < segmentCount + 1; segmentIndex += 1) {
    scores[0][segmentIndex] = scores[0][segmentIndex - 1] - getSegmentSkipPenalty(segments[segmentIndex - 1] as string[])
    steps[0][segmentIndex] = 'skip-segment'
  }

  for (let rowIndex = 1; rowIndex < rowCount + 1; rowIndex += 1) {
    for (let segmentIndex = 1; segmentIndex < segmentCount + 1; segmentIndex += 1) {
      const matchScore = scores[rowIndex - 1][segmentIndex - 1] + (matchScores[rowIndex - 1]?.[segmentIndex - 1] ?? -8)
      const skipRowScore = scores[rowIndex - 1][segmentIndex] - getRowSkipPenalty(rowProfiles[rowIndex - 1] as PropagationRowProfile)
      const skipSegmentScore = scores[rowIndex][segmentIndex - 1] - getSegmentSkipPenalty(segments[segmentIndex - 1] as string[])

      const matchWins = !(matchScore < skipRowScore) && !(matchScore < skipSegmentScore)
      const skipRowWins = !(skipRowScore < skipSegmentScore)

      if (matchWins) {
        scores[rowIndex][segmentIndex] = matchScore
        steps[rowIndex][segmentIndex] = 'match'
      } else if (skipRowWins) {
        scores[rowIndex][segmentIndex] = skipRowScore
        steps[rowIndex][segmentIndex] = 'skip-row'
      } else {
        scores[rowIndex][segmentIndex] = skipSegmentScore
        steps[rowIndex][segmentIndex] = 'skip-segment'
      }
    }
  }

  const assignments = new Map<string, number | null>()
  let rowIndex = rowCount
  let segmentIndex = segmentCount

  while (Math.max(rowIndex, segmentIndex) > 0) {
    const step = steps[rowIndex]?.[segmentIndex]
    if (step === 'match') {
      const profile = rowProfiles[rowIndex - 1] as PropagationRowProfile
      assignments.set(profile.rowId, segmentIndex - 1)
      rowIndex -= 1
      segmentIndex -= 1
      continue
    }
    if (step === 'skip-row') {
      const profile = rowProfiles[rowIndex - 1] as PropagationRowProfile
      assignments.set(profile.rowId, null)
      rowIndex -= 1
      continue
    }
    if (step === 'skip-segment') {
      segmentIndex -= 1
      continue
    }
    break
  }

  while (rowIndex > 0) {
    const profile = rowProfiles[rowIndex - 1] as PropagationRowProfile
    assignments.set(profile.rowId, null)
    rowIndex -= 1
  }

  return {
    score: scores[rowCount]?.[segmentCount] ?? Number.NEGATIVE_INFINITY,
    assignments: rowProfiles.map(profile => ({
      rowId: profile.rowId,
      segmentIndex: assignments.get(profile.rowId) ?? null
    }))
  }
}

const buildSequentialRowAssignments = (
  rowProfiles: PropagationRowProfile[],
  segments: string[][]
): WorkbenchPropagationRowAssignment[] => alignSegmentsToRows(rowProfiles, segments).assignments

const scoreSequentialRowAssignments = (
  rowProfiles: PropagationRowProfile[],
  segments: string[][]
): number => alignSegmentsToRows(rowProfiles, segments).score

const buildGreedyLedgerSplitIndices = ({
  rowProfiles,
  targetTokens,
  suggestedSplitSets
}: {
  rowProfiles: PropagationRowProfile[]
  targetTokens: string[]
  suggestedSplitSets: number[][]
}): number[] | null => {
  if (rowProfiles.length < 2 || targetTokens.length < 2) {
    return null
  }

  const totalMedianLength = rowProfiles.reduce((sum, profile) => sum + Math.max(1, profile.medianLength), 0)
  const remainingMedianLengths = new Array<number>(rowProfiles.length).fill(0)
  let runningMedianLength = 0
  for (let index = rowProfiles.length - 1; index > -1; index -= 1) {
    remainingMedianLengths[index] = runningMedianLength
    // mutation-ignore-next-line equivalent: rowProfiles[index] is in range and Math.max floors zero/undefined median lengths to one.
    runningMedianLength += Math.max(1, rowProfiles[index]?.medianLength || 1)
  }

  const expectedStarts: number[] = []
  let cumulativeExpected = 0
  rowProfiles.forEach(profile => {
    expectedStarts.push(
      Math.min(
        targetTokens.length,
        Math.max(0, Math.round((cumulativeExpected / Math.max(totalMedianLength, 1)) * targetTokens.length))
      )
    )
    cumulativeExpected += Math.max(1, profile.medianLength)
  })

  const splitIndices: number[] = []
  let currentStart = 0

  for (let rowIndex = 0; rowIndex < rowProfiles.length - 1; rowIndex += 1) {
    const profile = rowProfiles[rowIndex]
    if (!profile) {
      return null
    }

    const remainingRows = rowProfiles.length - rowIndex - 1
    const slack = Math.max(2, Math.ceil(profile.medianLength * 0.75))
    const minEnd = currentStart + 1
    const maxEnd = Math.min(
      targetTokens.length - remainingRows,
      currentStart + Math.max(profile.maxLength + slack, profile.medianLength + slack)
    )

    if (minEnd > maxEnd) {
      return null
    }

    const candidateEnds = new Set<number>()
    for (let endIndex = minEnd; endIndex < maxEnd + 1; endIndex += 1) {
      candidateEnds.add(endIndex)
    }
    suggestedSplitSets.forEach(suggestedSplits => {
      const suggestedEnd = suggestedSplits[rowIndex]
      if (!Number.isFinite(suggestedEnd)) {
        return
      }
      if (suggestedEnd < minEnd) {
        return
      }
      if (suggestedEnd > maxEnd) {
        return
      }
      candidateEnds.add(suggestedEnd)
    })

    let bestEndIndex: number | null = null
    let bestScore = Number.NEGATIVE_INFINITY
    const orderedCandidateEnds = Array.from(candidateEnds).sort((left, right) => left - right)
    for (const endIndex of orderedCandidateEnds) {
      const spanTokens = targetTokens.slice(currentStart, endIndex)
      const remainingTokenCount = targetTokens.length - endIndex
      const remainingMedianLength = remainingMedianLengths[rowIndex] ?? 0
      let score = scoreRowSpan({
        profile,
        spanTokens,
        startIndex: currentStart,
        totalTokenCount: targetTokens.length,
        expectedStart: expectedStarts[rowIndex] ?? currentStart
      })
      score -= Math.abs(remainingTokenCount - remainingMedianLength) * 0.35

      const suggestionMatches = suggestedSplitSets.reduce(
        (count, suggestedSplits) => count + (Object.is(suggestedSplits[rowIndex], endIndex) ? 1 : 0),
        0
      )
      score += suggestionMatches * 0.75

      if (bestEndIndex === null || score > bestScore) {
        bestEndIndex = endIndex
        bestScore = score
      }
    }

    if (bestEndIndex === null) {
      return null
    }

    splitIndices.push(bestEndIndex)
    currentStart = bestEndIndex
  }

  return normalizeSplitIndices(splitIndices, targetTokens.length)
}

const buildLedgerConditionedPlan = ({
  witnessPlan,
  exemplarPlans,
  rowProfiles,
  tuning
}: {
  witnessPlan: WitnessSplitPlan
  exemplarPlans: WitnessSplitPlan[]
  rowProfiles: PropagationRowProfile[]
  tuning: PropagationTuning
}): Pick<WorkbenchPropagationPlan, 'splitIndices' | 'segmentCount' | 'rowAssignments' | 'strategy'> | null => {
  const {
    witness,
    text: witnessText,
    segmentableWordSpans: witnessSegmentableWordSpans,
    normalizedTokens: targetTokens,
    existingSplitIndices
  } = witnessPlan

  if (rowProfiles.length === 0) {
    return null
  }
  if (targetTokens.length === 0) {
    return null
  }

  const candidateExemplars = filterLedgerConditionedCandidateExemplars(witness.id, exemplarPlans)
  const expectedSegmentCountCandidates = [
    witnessPlan.splitIndices.length + 1,
    ...candidateExemplars.map(exemplarPlan => exemplarPlan.existingSplitIndices.length + 1)
  ].filter(count => count > 0)
  const expectedSegmentCount = expectedSegmentCountCandidates.length > 0
    ? medianNumber(expectedSegmentCountCandidates)
    : rowProfiles.length
  const suggestedSplitSets = candidateExemplars
    .map(exemplarPlan => {
      let splitIndices = mapSplitIndicesByAnchors(
        exemplarPlan.existingSplitIndices,
        exemplarPlan.segmentableWordSpans,
        exemplarPlan.text.length,
        witnessSegmentableWordSpans,
        witnessText.length,
        tuning
      )

      if (existingSplitIndices.length > 0) {
        splitIndices = stabilizeSplitIndicesAgainstExisting({
          proposedSplitIndices: splitIndices,
          existingSplitIndices,
          maxWordCount: witnessSegmentableWordSpans.length,
          maxDriftWords: STABLE_BOUNDARY_MAX_WORD_DRIFT
        })
      }

      return normalizeSplitIndices(splitIndices, witnessSegmentableWordSpans.length)
    })
    .filter(splitIndices => splitIndices.length > 0)

  const greedySplitIndices = buildGreedyLedgerSplitIndices({
    rowProfiles,
    targetTokens,
    suggestedSplitSets
  })
  const baseSplitIndices = normalizeSplitIndices(witnessPlan.splitIndices, witnessSegmentableWordSpans.length)
  const candidateSplitSets = [
    ...(baseSplitIndices.length > 0 ? [baseSplitIndices] : []),
    ...(greedySplitIndices ? [greedySplitIndices] : []),
    ...suggestedSplitSets
  ].filter((splitIndices, index, collection) => (
    collection.findIndex(candidate => candidate.join(',') === splitIndices.join(',')) === index
  ))

  let bestSplitIndices: number[] | null = null
  let bestScore = Number.NEGATIVE_INFINITY
  candidateSplitSets.forEach(splitIndices => {
    const segments = derivePlanSegments(targetTokens, splitIndices)
    const score = scoreSequentialRowAssignments(rowProfiles, segments)
      - Math.abs(segments.length - expectedSegmentCount) * 4.2
    // mutation-ignore-next-line equivalent: bestScore starts at -Infinity, so the first candidate is still selected by the score branch.
    if (bestSplitIndices === null) {
      bestSplitIndices = splitIndices
      bestScore = score
      return
    }
    if (score > bestScore) {
      bestSplitIndices = splitIndices
      bestScore = score
    }
  })

  if (!bestSplitIndices) {
    return null
  }

  let chosenSplitIndices: number[] = [...bestSplitIndices]
  if (existingSplitIndices.length > 0) {
    chosenSplitIndices = stabilizeSplitIndicesAgainstExisting({
      proposedSplitIndices: chosenSplitIndices,
      existingSplitIndices,
      maxWordCount: witnessSegmentableWordSpans.length,
      maxDriftWords: STABLE_BOUNDARY_MAX_WORD_DRIFT
    })
  }

  const segments = derivePlanSegments(targetTokens, chosenSplitIndices)
  const rowAssignments = buildSequentialRowAssignments(rowProfiles, segments)

  return {
    splitIndices: chosenSplitIndices,
    segmentCount: segments.length,
    rowAssignments,
    strategy: 'ledger-conditioned'
  }
}

const filterLedgerConditionedCandidateExemplars = (
  targetWitnessId: string,
  exemplarPlans: WitnessSplitPlan[]
): WitnessSplitPlan[] => (
  exemplarPlans.filter(exemplarPlan => {
    if (exemplarPlan.witness.id === targetWitnessId) {
      return false
    }
    if (exemplarPlan.segmentableWordSpans.length === 0) {
      return false
    }
    if (exemplarPlan.existingSplitIndices.length === 0) {
      return false
    }
    return true
  })
)

const getSplitStartRatio = (plan: WitnessSplitPlan, splitIndex: number): number => {
  if (!Number.isFinite(splitIndex)) {
    return 0
  }
  if (splitIndex < 1) {
    return 0
  }
  if (!(splitIndex < plan.segmentableWordSpans.length)) {
    return 0
  }
  const splitSpan = plan.segmentableWordSpans[splitIndex]
  return splitSpan ? splitSpan.start / Math.max(plan.text.length, 1) : 0
}

const segmentLengthOutlierPenalty = (
  actualWords: number,
  expectedWords: number,
  tuning: PropagationTuning
): number => {
  if (actualWords < tuning.rebalanceMinSegmentWords) {
    return 1.8 + (tuning.rebalanceMinSegmentWords - actualWords) * 0.45
  }
  const safeExpected = Math.max(1, expectedWords)
  const ratio = actualWords / safeExpected
  if (ratio > tuning.rebalanceSegmentLongFactor) {
    return (ratio - tuning.rebalanceSegmentLongFactor) * 1.35
  }
  if (ratio < tuning.rebalanceSegmentShortFactor) {
    return (tuning.rebalanceSegmentShortFactor - ratio) * 1.35
  }
  return 0
}

const computeBoundaryOutlierPenalty = (
  candidateSplitIndex: number,
  boundaryIndex: number,
  currentSplits: number[],
  targetWordCount: number,
  groundTruthBoundaries: number[],
  tuning: PropagationTuning
): number => {
  const previousBoundary = boundaryIndex === 0 ? 0 : currentSplits[boundaryIndex - 1]
  const nextBoundary = boundaryIndex === currentSplits.length - 1 ? targetWordCount : currentSplits[boundaryIndex + 1]

  if (!Number.isFinite(previousBoundary) || !Number.isFinite(nextBoundary)) {
    return 4
  }
  if (candidateSplitIndex <= previousBoundary || candidateSplitIndex >= nextBoundary) {
    return 6
  }

  const groundTruthWordCount = groundTruthBoundaries[groundTruthBoundaries.length - 1] || 1
  const scale = targetWordCount / Math.max(groundTruthWordCount, 1)
  const expectedPreviousLength = Math.max(1, (groundTruthBoundaries[boundaryIndex + 1] - groundTruthBoundaries[boundaryIndex]) * scale)
  const expectedNextLength = Math.max(1, (groundTruthBoundaries[boundaryIndex + 2] - groundTruthBoundaries[boundaryIndex + 1]) * scale)

  const actualPreviousLength = candidateSplitIndex - previousBoundary
  const actualNextLength = nextBoundary - candidateSplitIndex

  return (
    segmentLengthOutlierPenalty(actualPreviousLength, expectedPreviousLength, tuning) +
    segmentLengthOutlierPenalty(actualNextLength, expectedNextLength, tuning)
  )
}

const rebalanceWitnessSplitPlans = ({
  plans,
  groundTruthSplitIndices,
  groundTruthSegmentableWordSpans,
  activeGroundTruthWitnessId,
  tuning
}: {
  plans: WitnessSplitPlan[]
  groundTruthSplitIndices: number[]
  groundTruthSegmentableWordSpans: WordSpan[]
  activeGroundTruthWitnessId: string
  tuning: PropagationTuning
}): WitnessSplitPlan[] => {
  if (plans.length === 0 || groundTruthSplitIndices.length === 0 || groundTruthSegmentableWordSpans.length <= 1) {
    return plans
  }

  const splitCount = groundTruthSplitIndices.length
  const groundTruthTokens = groundTruthSegmentableWordSpans.map(span => normalizePropagationToken(span.word))
  const groundTruthBoundaries = [0, ...groundTruthSplitIndices, groundTruthSegmentableWordSpans.length]
  const mutablePlans = plans.map(plan => ({ ...plan, splitIndices: [...plan.splitIndices] }))

  for (let pass = 0; pass < tuning.rebalancePasses; pass += 1) {
    const medianRatios = Array.from({ length: splitCount }, (_, boundaryIndex) => {
      const ratios = mutablePlans
        .map(plan => getSplitStartRatio(plan, plan.splitIndices[boundaryIndex]))
        .filter(value => Number.isFinite(value))
      const median = medianNumber(ratios)
      return Number.isFinite(median) ? median : 0
    })

    mutablePlans.forEach(plan => {
      if (plan.witness.id === activeGroundTruthWitnessId || plan.segmentableWordSpans.length <= 1) {
        return
      }

      if (plan.splitIndices.length !== splitCount) {
        const normalized = normalizeSplitIndices(plan.splitIndices, plan.segmentableWordSpans.length)
        if (normalized.length !== splitCount) {
          return
        }
        plan.splitIndices = normalized
      }

      const nextSplits = [...plan.splitIndices]
      const targetWordCount = plan.segmentableWordSpans.length

      for (let boundaryIndex = 0; boundaryIndex < splitCount; boundaryIndex += 1) {
        const currentSplit = nextSplits[boundaryIndex]
        if (!Number.isFinite(currentSplit)) {
          continue
        }

        const minSplit = boundaryIndex === 0 ? 1 : nextSplits[boundaryIndex - 1] + 1
        const maxSplit = boundaryIndex === splitCount - 1 ? targetWordCount - 1 : nextSplits[boundaryIndex + 1] - 1
        if (minSplit > maxSplit) {
          continue
        }

        const searchStart = Math.max(minSplit, currentSplit - tuning.rebalanceSearchWindow)
        const searchEnd = Math.min(maxSplit, currentSplit + tuning.rebalanceSearchWindow)
        const expectedRatio = medianRatios[boundaryIndex] || getSplitStartRatio(plan, currentSplit)

        let bestSplit = currentSplit
        let bestScore = Number.NEGATIVE_INFINITY

        for (let candidate = searchStart; candidate <= searchEnd; candidate += 1) {
          const ratio = getSplitStartRatio(plan, candidate)
          const ratioPenalty = Math.abs(ratio - expectedRatio) * tuning.rebalanceRatioWeight
          const anchor = scoreSplitAnchorAlignment(
            groundTruthTokens,
            plan.normalizedTokens,
            groundTruthSplitIndices[boundaryIndex],
            candidate
          )
          const anchorBonus = anchor.compared > 0 ? (anchor.score / anchor.compared) * tuning.rebalanceAnchorWeight : 0
          const movePenalty = Math.abs(candidate - currentSplit) * tuning.rebalanceMovePenalty
          const outlierPenalty = computeBoundaryOutlierPenalty(
            candidate,
            boundaryIndex,
            nextSplits,
            targetWordCount,
            groundTruthBoundaries,
            tuning
          )
          const candidateScore = anchorBonus - ratioPenalty - movePenalty - outlierPenalty
          if (candidateScore > bestScore) {
            bestScore = candidateScore
            bestSplit = candidate
          }
        }

        nextSplits[boundaryIndex] = bestSplit
      }

      plan.splitIndices = nextSplits
    })
  }

  return mutablePlans.map(plan => ({
    ...plan,
    splitIndices: normalizeSplitIndices(plan.splitIndices, plan.segmentableWordSpans.length)
  }))
}

export const stabilizeSplitIndicesAgainstExisting = ({
  proposedSplitIndices,
  existingSplitIndices,
  maxWordCount,
  maxDriftWords
}: {
  proposedSplitIndices: number[]
  existingSplitIndices: number[]
  maxWordCount: number
  maxDriftWords: number
}): number[] => {
  const normalizedProposed = normalizeSplitIndices(proposedSplitIndices, maxWordCount)
  const normalizedExisting = normalizeSplitIndices(existingSplitIndices, maxWordCount)
  if (normalizedProposed.length === 0 || normalizedExisting.length === 0) {
    return normalizedProposed
  }
  if (normalizedProposed.length !== normalizedExisting.length) {
    return normalizedProposed
  }

  const stabilized: number[] = []
  for (let boundaryIndex = 0; boundaryIndex < normalizedProposed.length; boundaryIndex += 1) {
    const proposed = normalizedProposed[boundaryIndex]
    const existing = normalizedExisting[boundaryIndex]
    const minByDrift = Math.max(1, existing - maxDriftWords)
    const maxByDrift = Math.min(maxWordCount - 1, existing + maxDriftWords)
    const minByOrder = boundaryIndex === 0 ? 1 : stabilized[boundaryIndex - 1] + 1
    const remainingBoundaries = normalizedExisting.length - boundaryIndex - 1
    const maxByOrder = Math.max(minByOrder, (maxWordCount - 1) - remainingBoundaries)
    const lowerBound = Math.max(minByDrift, minByOrder)
    const upperBound = Math.min(maxByDrift, maxByOrder)
    if (lowerBound > upperBound) {
      stabilized.push(Math.max(minByOrder, Math.min(maxByOrder, existing)))
      continue
    }
    stabilized.push(Math.max(lowerBound, Math.min(upperBound, proposed)))
  }

  return normalizeSplitIndices(stabilized, maxWordCount)
}

export const buildWorkbenchPropagationPreview = ({
  sourceWitness,
  candidateWitnesses,
  groundTruthWitnessId,
  rows = [],
  options = {},
  tuning = DEFAULT_PROPAGATION_TUNING
}: {
  sourceWitness: Witness
  candidateWitnesses: Witness[]
  groundTruthWitnessId?: string | null
  rows?: AnchoredRow[]
  options?: WorkbenchPropagationPreviewOptions
  tuning?: PropagationTuning
}): WorkbenchPropagationPreview => {
  const tokenCache = createPropagationTokenCache()
  const sourceText = sourceWitness.uploaded_file?.content || ''
  const sourceWordSpans = extractWordSpans(sourceText)
  const sourceFoliationSpans = extractFoliationSpans(sourceText, sourceWordSpans)
  const sourceSegmentableWordSpans = getSegmentableWordData(sourceWordSpans, sourceFoliationSpans).segmentableWordSpans
  const sourceSplitIndices = deriveSplitIndicesFromSegmentation(sourceWitness.segmentation_result, sourceSegmentableWordSpans)
  const usesLedgerProfiles = rows.length > 0

  if ((!usesLedgerProfiles && sourceSegmentableWordSpans.length === 0) || (!usesLedgerProfiles && sourceSplitIndices.length === 0)) {
    return {
      sourceWitness,
      sourceSplitIndices: [],
      segmentCount: 0,
      plans: [],
      protectedWitnessIds: new Set(),
      mode: 'proposal',
    }
  }

  const baseTargetSelection = selectPropagationTargets({
    candidates: candidateWitnesses.filter(witness => Boolean(witness.uploaded_file?.content?.trim())),
    activeEditWitnessId: sourceWitness.id,
    groundTruthWitnessId
  })
  const repropagatableWitnessIds = new Set(
    [
      ...candidateWitnesses
        .filter(witness => witness.id !== sourceWitness.id && isAutoPropagatedSegmentation(witness.segmentation_result))
        .map(witness => witness.id),
      ...(options.allowExistingSegmentationWitnessIds || []),
    ].filter(witnessId => witnessId && witnessId !== sourceWitness.id)
  )
  const ledgerProtectedWitnessIds = usesLedgerProfiles ? getProtectedWitnessIdsFromRows(rows, sourceWitness.id) : new Set<string>()
  const protectedWitnessIds = new Set<string>([
    ...Array.from(baseTargetSelection.protectedWitnessIds).filter(witnessId => !repropagatableWitnessIds.has(witnessId)),
    ...Array.from(ledgerProtectedWitnessIds).filter(witnessId => !repropagatableWitnessIds.has(witnessId))
  ])
  const planByWitnessId = new Map(
    candidateWitnesses
      .filter(witness => Boolean(witness.uploaded_file?.content?.trim()))
      .map(witness => [witness.id, buildWitnessSplitPlan(witness)])
  )
  const targetWitnesses = candidateWitnesses
    .filter(witness => Boolean(witness.uploaded_file?.content?.trim()))
    .filter(witness => witness.id !== sourceWitness.id && !protectedWitnessIds.has(witness.id))

  const initialPlans: WitnessSplitPlan[] = targetWitnesses.map(witness => {
    const basePlan = planByWitnessId.get(witness.id) || buildWitnessSplitPlan(witness)
    return {
      ...basePlan,
      splitIndices: mapSplitIndicesByAnchors(
          sourceSplitIndices,
          sourceSegmentableWordSpans,
          sourceText.length,
          basePlan.segmentableWordSpans,
          basePlan.text.length,
          tuning
        )
    }
  })

  const rebalancedPlans = usesLedgerProfiles
    ? initialPlans
    : rebalanceWitnessSplitPlans({
      plans: initialPlans,
      groundTruthSplitIndices: sourceSplitIndices,
      groundTruthSegmentableWordSpans: sourceSegmentableWordSpans,
      activeGroundTruthWitnessId: sourceWitness.id,
      tuning
    })

  const stabilizedPlans = rebalancedPlans.map(plan => {
    if (plan.existingSplitIndices.length === 0) {
      return plan
    }
    return {
      ...plan,
      splitIndices: stabilizeSplitIndicesAgainstExisting({
        proposedSplitIndices: plan.splitIndices,
        existingSplitIndices: plan.existingSplitIndices,
        maxWordCount: plan.segmentableWordSpans.length,
        maxDriftWords: STABLE_BOUNDARY_MAX_WORD_DRIFT
      })
    }
  })

  return {
    sourceWitness,
    sourceSplitIndices,
    segmentCount: usesLedgerProfiles ? sortRows(rows).length : sourceSplitIndices.length + 1,
    mode: 'proposal',
    plans: candidateWitnesses.map(witness => {
      if (witness.id === sourceWitness.id) {
        return {
          witness,
          splitIndices: sourceSplitIndices,
          segmentCount: sourceSplitIndices.length + (sourceSplitIndices.length > 0 ? 1 : 0),
          protected: false,
          strategy: 'single-source' as const
        }
      }

      const plan = stabilizedPlans.find(candidate => candidate.witness.id === witness.id)
      const exemplarWitnessIds = usesLedgerProfiles
        ? rankPropagationExemplarWitnessIds({
          targetWitness: witness,
          candidateWitnesses,
          sourceWitnessId: sourceWitness.id,
          tokenCache
        }).slice(0, 2)
        : []
      const rowProfiles = usesLedgerProfiles
        ? buildPropagationRowProfiles({
          rows,
          preferredWitnessIds: [sourceWitness.id, ...exemplarWitnessIds],
          tokenCache
        })
        : []
      const exemplarPlans = exemplarWitnessIds
        .map(exemplarWitnessId => planByWitnessId.get(exemplarWitnessId))
        .filter((candidatePlan): candidatePlan is WitnessSplitPlan => Boolean(candidatePlan))
      const ledgerConditionedPlan = usesLedgerProfiles && !protectedWitnessIds.has(witness.id) && witness.id !== sourceWitness.id
        ? buildLedgerConditionedPlan({
          witnessPlan: plan || buildWitnessSplitPlan(witness),
          exemplarPlans,
          rowProfiles,
          tuning
        })
        : null
      const splitIndices = ledgerConditionedPlan?.splitIndices ?? plan?.splitIndices ?? []
      const segmentCount = ledgerConditionedPlan
        ? ledgerConditionedPlan.segmentCount
        : (plan && plan.segmentableWordSpans.length > 0 ? plan.splitIndices.length + 1 : 0)
      return {
        witness,
        splitIndices,
        segmentCount,
        protected: protectedWitnessIds.has(witness.id),
        rowAssignments: ledgerConditionedPlan?.rowAssignments,
        exemplarWitnessIds,
        strategy: ledgerConditionedPlan?.strategy || (plan ? 'single-source' : undefined)
      }
    }),
    protectedWitnessIds
  }
}

export const buildWorkbenchPropagationUpdates = ({
  preview,
  contextId,
  options
}: {
  preview: WorkbenchPropagationPreview
  contextId: string
  options?: WorkbenchPropagationApplyOptions
}): Array<{
  witnessId: string
  type: 'segmentation'
  result: CompleteMicroSegmentationResult
  status?: Witness['processing_status']
}> => {
  const updates: Array<{
    witnessId: string
    type: 'segmentation'
    result: CompleteMicroSegmentationResult
    status?: Witness['processing_status']
  }> = []
  const selectedWitnessIdSet = options?.selectedWitnessIds?.length
    ? new Set(options.selectedWitnessIds)
    : null

  preview.plans.forEach(plan => {
    if (
      plan.protected ||
      plan.witness.id === preview.sourceWitness.id ||
      plan.segmentCount === 0 ||
      (selectedWitnessIdSet && !selectedWitnessIdSet.has(plan.witness.id))
    ) {
      return
    }
    const text = plan.witness.uploaded_file?.content || ''
    const result = buildManualSegmentationResult({
      text,
      witnessId: plan.witness.id,
      witnessSigla: plan.witness.sigla,
      clusterId: `workbench:${contextId}`,
      sourceGroundTruthSigla: preview.sourceWitness.sigla,
      splitIndices: plan.splitIndices,
      propagated: true,
      previousSegmentationResult: plan.witness.segmentation_result
    })
    updates.push({
      witnessId: plan.witness.id,
      type: 'segmentation',
      result,
      ...(plan.witness.processing_status !== 'completed' ? { status: 'completed' as Witness['processing_status'] } : {})
    })
  })

  return updates
}

const sortRows = (rows: AnchoredRow[]): AnchoredRow[] => [...rows].sort((left, right) => {
  if (left.position !== right.position) {
    return left.position - right.position
  }
  return left.labelNumber - right.labelNumber
})

const getOrderedUnits = (
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
): NonNullable<CompleteMicroSegmentationResult['chunk_results']>[number]['micro_units'] => {
  if (!segmentationResult?.chunk_results?.length) {
    return []
  }

  return segmentationResult.chunk_results
    .flatMap(chunk => Array.isArray(chunk?.micro_units) ? chunk.micro_units : [])
    .sort((left, right) => {
      const leftStart = typeof left?.start_position === 'number' ? left.start_position : Number.MAX_SAFE_INTEGER
      const rightStart = typeof right?.start_position === 'number' ? right.start_position : Number.MAX_SAFE_INTEGER
      if (leftStart !== rightStart) {
        return leftStart - rightStart
      }
      const leftEnd = typeof left?.end_position === 'number' ? left.end_position : Number.MAX_SAFE_INTEGER
      const rightEnd = typeof right?.end_position === 'number' ? right.end_position : Number.MAX_SAFE_INTEGER
      if (leftEnd !== rightEnd) {
        return leftEnd - rightEnd
      }
      return String(left?.unit_uuid || left?.unit_id || '').localeCompare(String(right?.unit_uuid || right?.unit_id || ''))
    })
}

const getActiveMapping = (row: AnchoredRow, witnessId: string): UnitMapping | undefined =>
  row.unitMappings.find(mapping => mapping.witnessId === witnessId && !mapping.excluded)

const hasExcludedWitnessMapping = (row: AnchoredRow, witnessId: string): boolean =>
  row.unitMappings.some(mapping => mapping.witnessId === witnessId && Boolean(mapping.excluded))

const replaceWitnessMappings = (row: AnchoredRow, witnessId: string, nextMappings: UnitMapping[]): AnchoredRow => ({
  ...row,
  unitMappings: [
    ...row.unitMappings.filter(mapping => mapping.witnessId !== witnessId),
    ...nextMappings
  ],
  status: row.status === 'confirmed' ? 'needs-review' : row.status,
  updatedAt: new Date().toISOString()
})

const deriveSelectedRepairScopedRowAssignments = ({
  preview,
  rows,
}: {
  preview: SelectedUnitPropagationRepairPreview
  rows: AnchoredRow[]
}): {
  scopedRowAssignments: WorkbenchPropagationRowAssignment[]
  outOfScopeAssignmentRowIds: string[]
  outsideLocalContextAssignmentRowIds: string[]
} => {
  const staticScopeRowIdSet = new Set([
    ...preview.selectedRowIds,
    ...preview.contextRowIds,
    ...preview.anchorRowIds,
  ])
  const sortedRows = sortRows(rows)
  const rowById = new Map(sortedRows.map(row => [row.id, row]))
  const rowPositionById = new Map(sortedRows.map((row, index) => [
    row.id,
    typeof row.position === 'number' ? row.position : index,
  ]))
  const structurallyValidAssignments: WorkbenchPropagationRowAssignment[] = []
  const outOfScopeAssignmentRowIds = new Set<string>()

  preview.rowAssignments.forEach(assignment => {
    const row = rowById.get(assignment.rowId)
    const isGapAssignment = assignment.translationStatus === 'gap' && assignment.segmentIndex === null
    const hasMappedSegment = typeof assignment.segmentIndex === 'number'
    const interval = preview.selectedRepairTokenInterval
    const hasTokenRange = (
      typeof assignment.startTokenIndex === 'number'
      && typeof assignment.endTokenIndex === 'number'
    )
    const tokenRangeIsInsideRepair = Boolean(
      interval
      && hasTokenRange
      && assignment.startTokenIndex! >= interval.startTokenIndex
      && assignment.endTokenIndex! < interval.endTokenIndex
      && assignment.endTokenIndex! >= assignment.startTokenIndex!
    )
    const wouldOverwriteAdmittedMapping = Boolean(
      row
      && !staticScopeRowIdSet.has(assignment.rowId)
      && getActiveMapping(row, preview.targetWitness.id)
    )
    const structurallyValid = Boolean(
      row
      && !wouldOverwriteAdmittedMapping
      && (
        isGapAssignment
        || (hasMappedSegment && tokenRangeIsInsideRepair)
      )
    )
    if (structurallyValid) {
      structurallyValidAssignments.push(assignment)
    } else {
      outOfScopeAssignmentRowIds.add(assignment.rowId)
    }
  })

  const assignmentEntries = structurallyValidAssignments
    .map((assignment, originalIndex) => ({
      assignment,
      originalIndex,
      position: rowPositionById.get(assignment.rowId),
    }))
    .filter((entry): entry is {
      assignment: WorkbenchPropagationRowAssignment
      originalIndex: number
      position: number
    } => typeof entry.position === 'number')
    .sort((left, right) => {
      if (left.position !== right.position) {
        return left.position - right.position
      }
      return left.originalIndex - right.originalIndex
    })
  const allowedAssignmentRowIds = new Set<string>()
  let cluster: typeof assignmentEntries = []

  const flushCluster = () => {
    if (cluster.length === 0) {
      return
    }
    const touchesStaticScope = cluster.some(entry => staticScopeRowIdSet.has(entry.assignment.rowId))
    if (touchesStaticScope) {
      cluster.forEach(entry => allowedAssignmentRowIds.add(entry.assignment.rowId))
    }
    cluster = []
  }

  assignmentEntries.forEach(entry => {
    const previous = cluster[cluster.length - 1]
    if (
      previous
      && entry.position - previous.position > SELECTED_REPAIR_ASSIGNMENT_CLUSTER_MAX_GAP
    ) {
      flushCluster()
    }
    cluster.push(entry)
  })
  flushCluster()

  const outsideLocalContextAssignmentRowIds = Array.from(new Set(
    structurallyValidAssignments
      .map(assignment => assignment.rowId)
      .filter(rowId => !staticScopeRowIdSet.has(rowId) && !allowedAssignmentRowIds.has(rowId))
  ))

  return {
    scopedRowAssignments: structurallyValidAssignments,
    outOfScopeAssignmentRowIds: Array.from(outOfScopeAssignmentRowIds),
    outsideLocalContextAssignmentRowIds,
  }
}

const getSegmentableWordSpansForWitness = (witness: Witness): WordSpan[] => {
  const text = witness.uploaded_file?.content || ''
  const wordSpans = extractWordSpans(text)
  const foliationSpans = extractFoliationSpans(text, wordSpans)
  return getSegmentableWordData(wordSpans, foliationSpans).segmentableWordSpans
}

const deriveSelectedRepairTokenInterval = ({
  targetWitness,
  rows,
  selectedRowIds,
  segmentableWordSpans,
  existingSplitIndices,
}: {
  targetWitness: Witness
  rows: AnchoredRow[]
  selectedRowIds: string[]
  segmentableWordSpans: WordSpan[]
  existingSplitIndices: number[]
}): { startTokenIndex: number; endTokenIndex: number } | null => {
  const selectedRowIdSet = new Set(selectedRowIds)
  const selectedRows = sortRows(rows).filter(row => selectedRowIdSet.has(row.id))
  const orderedUnits = getOrderedUnits(targetWitness.segmentation_result)
  const unitIndexLookup = buildUnitReferenceIndexWithRemap({
    units: orderedUnits,
    idRemap: targetWitness.segmentation_result?.id_remap,
  })
  const selectedUnitIndexes = selectedRows
    .map(row => {
      const mapping = getActiveMapping(row, targetWitness.id)
      return mapping ? unitIndexLookup.get(mapping.unitUuid) : undefined
    })
    .filter((index): index is number => typeof index === 'number')

  if (selectedUnitIndexes.length === 0 || segmentableWordSpans.length === 0) {
    return null
  }

  const firstUnitIndex = Math.min(...selectedUnitIndexes)
  const lastUnitIndex = Math.max(...selectedUnitIndexes)
  const firstUnit = orderedUnits[firstUnitIndex]
  const lastUnit = orderedUnits[lastUnitIndex]
  const charStart = typeof firstUnit?.start_position === 'number' ? firstUnit.start_position : null
  const charEnd = typeof lastUnit?.end_position === 'number' ? lastUnit.end_position : null

  if (typeof charStart === 'number' && typeof charEnd === 'number' && charEnd > charStart) {
    const startTokenIndex = segmentableWordSpans.findIndex(span => span.end > charStart)
    let endTokenIndex = -1
    segmentableWordSpans.forEach((span, index) => {
      if (span.start < charEnd) {
        endTokenIndex = index + 1
      }
    })
    if (startTokenIndex >= 0 && endTokenIndex > startTokenIndex) {
      return {
        startTokenIndex,
        endTokenIndex,
      }
    }
  }

  const startTokenIndex = firstUnitIndex <= 0 ? 0 : existingSplitIndices[firstUnitIndex - 1]
  const endTokenIndex = lastUnitIndex < existingSplitIndices.length
    ? existingSplitIndices[lastUnitIndex]
    : segmentableWordSpans.length
  if (
    typeof startTokenIndex !== 'number'
    || typeof endTokenIndex !== 'number'
    || endTokenIndex <= startTokenIndex
  ) {
    return null
  }

  return {
    startTokenIndex,
    endTokenIndex,
  }
}

const deriveTokenIntervalFromCharRange = ({
  segmentableWordSpans,
  startChar,
  endChar,
}: {
  segmentableWordSpans: WordSpan[]
  startChar: number
  endChar: number
}): { startTokenIndex: number; endTokenIndex: number } | null => {
  if (segmentableWordSpans.length === 0 || endChar <= startChar) {
    return null
  }
  const startTokenIndex = segmentableWordSpans.findIndex(span => span.end > startChar)
  let endTokenIndex = -1
  segmentableWordSpans.forEach((span, index) => {
    if (span.start < endChar) {
      endTokenIndex = index + 1
    }
  })
  if (startTokenIndex < 0 || endTokenIndex <= startTokenIndex) {
    return null
  }
  return {
    startTokenIndex,
    endTokenIndex,
  }
}

const deriveTokenIntervalCharRange = (
  segmentableWordSpans: WordSpan[],
  interval: { startTokenIndex: number; endTokenIndex: number } | undefined
): { start: number; end: number } | null => {
  if (!interval) {
    return null
  }
  const startSpan = segmentableWordSpans[interval.startTokenIndex]
  const endSpan = segmentableWordSpans[interval.endTokenIndex - 1]
  if (!startSpan || !endSpan || endSpan.end <= startSpan.start) {
    return null
  }
  return {
    start: startSpan.start,
    end: endSpan.end,
  }
}

const normalizeUnassignedFragmentTextKey = (value: string): string =>
  String(value || '').replace(/\s+/g, ' ').trim()

const buildUnassignedFragmentSpanKey = (fragment: LedgerSafeUnassignedFragment): string =>
  [
    Number.isFinite(fragment.start) ? fragment.start : 0,
    Number.isFinite(fragment.end) ? fragment.end : 0,
    normalizeUnassignedFragmentTextKey(fragment.text),
  ].join(':')

const overlapsSelectedRepairCharRange = (
  fragment: LedgerSafeUnassignedFragment,
  repairCharRange: { start: number; end: number } | null
): boolean => (
  repairCharRange !== null
  && fragment.end > repairCharRange.start
  && fragment.start < repairCharRange.end
)

const unitOverlapsCharRange = (
  unit: MicroUnit,
  range: { start: number; end: number }
): boolean => {
  const start = Number(unit.start_position)
  const end = Number(unit.end_position)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return false
  }
  return end > range.start && start < range.end
}

const cloneMicroUnit = (unit: MicroUnit): MicroUnit => ({
  ...unit,
  ...(unit.processing_metadata ? { processing_metadata: { ...unit.processing_metadata } } : {}),
})

const normalizeIdRemapEntries = (idRemap: unknown): Record<string, string[]> => {
  if (!idRemap || typeof idRemap !== 'object') {
    return {}
  }

  return Object.entries(idRemap as Record<string, unknown>).reduce<Record<string, string[]>>((accumulator, [source, targets]) => {
    const sourceKey = String(source || '').trim()
    if (!sourceKey || !Array.isArray(targets)) {
      return accumulator
    }
    const normalizedTargets = Array.from(new Set(
      targets
        .map(target => String(target || '').trim())
        .filter(Boolean)
    ))
    if (normalizedTargets.length > 0) {
      accumulator[sourceKey] = normalizedTargets
    }
    return accumulator
  }, {})
}

const addIdRemapTarget = (idRemap: Record<string, string[]>, source: string | undefined, target: string): void => {
  const sourceKey = String(source || '').trim()
  const targetKey = String(target || '').trim()
  if (!sourceKey || !targetKey || sourceKey === targetKey) {
    return
  }
  const existingTargets = idRemap[sourceKey] || []
  if (!existingTargets.includes(targetKey)) {
    idRemap[sourceKey] = [...existingTargets, targetKey]
  }
}

const mergeIdRemapEntries = (...entries: Array<Record<string, string[]> | undefined>): Record<string, string[]> => {
  const merged: Record<string, string[]> = {}
  entries.forEach(entry => {
    Object.entries(entry || {}).forEach(([source, targets]) => {
      targets.forEach(target => addIdRemapTarget(merged, source, target))
    })
  })
  return merged
}

const getScopedRepairUnitLabel = (contextId: string, index: number): string =>
  `Cworkbench:${contextId}.${index + 1}`

const normalizeScopedRepairUnits = ({
  units,
  contextId,
  chunkId,
  idRemap,
}: {
  units: MicroUnit[]
  contextId: string
  chunkId: string
  idRemap: Record<string, string[]>
}): MicroUnit[] => units.map((unit, index) => {
  const previousReferences = [unit.unit_uuid, unit.unit_id, unit.unit_label]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .map(value => value.trim())
  const unitUuid = String(unit.unit_uuid || unit.unit_id || `${chunkId}_uuid_${index + 1}`).trim()
  const nextUnitId = `${chunkId}_U${index + 1}`
  const nextUnitLabel = getScopedRepairUnitLabel(contextId, index)
  previousReferences.forEach(reference => addIdRemapTarget(idRemap, reference, unitUuid))

  return {
    ...unit,
    unit_uuid: unitUuid,
    unit_id: nextUnitId,
    unit_number: index + 1,
    source_chunk_id: chunkId,
    parent_primary_chunk: 'P0',
    unit_label: nextUnitLabel,
    chunk_relative_start: unit.start_position,
    chunk_relative_end: unit.end_position,
    unit_length_chars: Math.max(0, Number(unit.end_position || 0) - Number(unit.start_position || 0)),
  }
})

const buildSelectedRepairScopedSegmentationResult = ({
  preview,
  contextId,
  repairCharRange,
}: {
  preview: SelectedUnitPropagationRepairPreview
  contextId: string
  repairCharRange: { start: number; end: number } | null
}): CompleteMicroSegmentationResult => {
  const fullRepairResult = buildManualSegmentationResult({
    text: preview.targetWitness.uploaded_file?.content || '',
    witnessId: preview.targetWitness.id,
    witnessSigla: preview.targetWitness.sigla,
    clusterId: `workbench:${contextId}`,
    sourceGroundTruthSigla: preview.targetWitness.sigla,
    splitIndices: preview.splitIndices,
    propagated: true,
    previousSegmentationResult: preview.targetWitness.segmentation_result,
  })
  const previousResult = preview.targetWitness.segmentation_result
  if (!previousResult || !repairCharRange) {
    return fullRepairResult
  }

  const repairUnits = getOrderedUnits(fullRepairResult)
    .filter(unit => unitOverlapsCharRange(unit, repairCharRange))
    .map(cloneMicroUnit)
  if (repairUnits.length === 0) {
    return fullRepairResult
  }

  const preservedUnits = getOrderedUnits(previousResult)
    .filter(unit => !unitOverlapsCharRange(unit, repairCharRange))
    .map(cloneMicroUnit)
  const combinedUnits = [...preservedUnits, ...repairUnits].sort((left, right) => {
    const leftStart = typeof left.start_position === 'number' ? left.start_position : Number.MAX_SAFE_INTEGER
    const rightStart = typeof right.start_position === 'number' ? right.start_position : Number.MAX_SAFE_INTEGER
    if (leftStart !== rightStart) {
      return leftStart - rightStart
    }
    const leftEnd = typeof left.end_position === 'number' ? left.end_position : Number.MAX_SAFE_INTEGER
    const rightEnd = typeof right.end_position === 'number' ? right.end_position : Number.MAX_SAFE_INTEGER
    if (leftEnd !== rightEnd) {
      return leftEnd - rightEnd
    }
    return String(left.unit_uuid || left.unit_id || '').localeCompare(String(right.unit_uuid || right.unit_id || ''))
  })
  const chunkTemplate = fullRepairResult.chunk_results[0] || previousResult.chunk_results?.[0]
  const chunkId = chunkTemplate?.source_chunk_id || `manual_cluster_workbench:${contextId}`
  const mergedIdRemap = mergeIdRemapEntries(
    normalizeIdRemapEntries(previousResult.id_remap),
    normalizeIdRemapEntries(fullRepairResult.id_remap),
  )
  const normalizedUnits = normalizeScopedRepairUnits({
    units: combinedUnits,
    contextId,
    chunkId,
    idRemap: mergedIdRemap,
  })

  return {
    ...fullRepairResult,
    segmentation_metadata: {
      ...fullRepairResult.segmentation_metadata,
      scoped_selected_repair: true,
      selected_repair_token_interval: preview.selectedRepairTokenInterval || null,
      selected_repair_preserved_unit_count: preservedUnits.length,
      selected_repair_rebuilt_unit_count: repairUnits.length,
    },
    chunk_results: [{
      ...(chunkTemplate || {
        source_chunk_id: chunkId,
        parent_primary_chunk: 'P0',
        processing_time: 0,
        processing_status: 'success' as const,
        fallback_used: false,
      }),
      source_chunk_id: chunkId,
      parent_primary_chunk: 'P0',
      processing_time: chunkTemplate?.processing_time || 0,
      processing_status: 'success',
      fallback_used: false,
      micro_units: normalizedUnits,
      chunk_position_data: {
        start_position: 0,
        end_position: preview.targetWitness.uploaded_file?.content?.length || 0,
        relative_start: 0,
        relative_end: preview.targetWitness.uploaded_file?.content?.length || 0,
      },
    }],
    aggregated_statistics: {
      ...fullRepairResult.aggregated_statistics,
      total_units: normalizedUnits.length,
      total_chunks: 1,
    },
    ...(Object.keys(mergedIdRemap).length > 0 ? { id_remap: mergedIdRemap } : {}),
  }
}

const findSegmentIndexForAssignmentInResult = ({
  assignment,
  result,
  segmentableWordSpans,
}: {
  assignment: WorkbenchPropagationRowAssignment
  result: CompleteMicroSegmentationResult
  segmentableWordSpans: WordSpan[]
}): number | null => {
  if (
    typeof assignment.startTokenIndex !== 'number'
    || typeof assignment.endTokenIndex !== 'number'
  ) {
    return assignment.segmentIndex
  }
  const charRange = deriveTokenIntervalCharRange(segmentableWordSpans, {
    startTokenIndex: assignment.startTokenIndex,
    endTokenIndex: assignment.endTokenIndex + 1,
  })
  if (!charRange) {
    return assignment.segmentIndex
  }
  let bestIndex: number | null = null
  let bestOverlap = 0
  getOrderedUnits(result).forEach((unit, index) => {
    const start = Number(unit.start_position)
    const end = Number(unit.end_position)
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      return
    }
    const overlap = Math.max(0, Math.min(end, charRange.end) - Math.max(start, charRange.start))
    if (overlap > bestOverlap) {
      bestOverlap = overlap
      bestIndex = index
    }
  })
  return bestOverlap > 0 ? bestIndex : assignment.segmentIndex
}

const remapRowAssignmentsToSegmentationResult = ({
  rowAssignments,
  result,
  segmentableWordSpans,
}: {
  rowAssignments: WorkbenchPropagationRowAssignment[]
  result: CompleteMicroSegmentationResult
  segmentableWordSpans: WordSpan[]
}): WorkbenchPropagationRowAssignment[] => rowAssignments.map(assignment => ({
  ...assignment,
  segmentIndex: findSegmentIndexForAssignmentInResult({
    assignment,
    result,
    segmentableWordSpans,
  }),
}))

const mergeSelectedRepairSplitIndices = ({
  existingSplitIndices,
  proposedSplitIndices,
  selectedStartTokenIndex,
  selectedEndTokenIndex,
  totalTokenCount,
}: {
  existingSplitIndices: number[]
  proposedSplitIndices: number[]
  selectedStartTokenIndex: number
  selectedEndTokenIndex: number
  totalTokenCount: number
}): number[] => {
  const outsideExisting = existingSplitIndices.filter(index => (
    index <= selectedStartTokenIndex || index >= selectedEndTokenIndex
  ))
  const insideProposed = proposedSplitIndices.filter(index => (
    index > selectedStartTokenIndex && index < selectedEndTokenIndex
  ))
  const selectedBoundaries = [
    selectedStartTokenIndex > 0 ? selectedStartTokenIndex : null,
    selectedEndTokenIndex < totalTokenCount ? selectedEndTokenIndex : null,
  ].filter((index): index is number => typeof index === 'number')

  return normalizeSplitIndices([
    ...outsideExisting,
    ...selectedBoundaries,
    ...insideProposed,
  ], totalTokenCount)
}

const findSegmentIndexForTokenRange = ({
  splitIndices,
  totalTokenCount,
  startTokenIndex,
  endTokenIndex,
}: {
  splitIndices: number[]
  totalTokenCount: number
  startTokenIndex: number
  endTokenIndex: number
}): number | null => {
  const boundaries = [0, ...normalizeSplitIndices(splitIndices, totalTokenCount), totalTokenCount]
  const endExclusive = endTokenIndex + 1
  let bestIndex: number | null = null
  let bestOverlap = 0

  for (let index = 0; index < boundaries.length - 1; index += 1) {
    const segmentStart = boundaries[index]
    const segmentEnd = boundaries[index + 1]
    if (typeof segmentStart !== 'number' || typeof segmentEnd !== 'number') {
      continue
    }
    if (startTokenIndex >= segmentStart && endExclusive <= segmentEnd) {
      return index
    }
    const overlap = Math.max(0, Math.min(endExclusive, segmentEnd) - Math.max(startTokenIndex, segmentStart))
    if (overlap > bestOverlap) {
      bestOverlap = overlap
      bestIndex = index
    }
  }

  return bestOverlap > 0 ? bestIndex : null
}

export const buildSelectedUnitPropagationRepairPreview = ({
  targetWitness,
  rows,
  selectedRowIds,
  contextRowIds,
  anchorRowIds,
  proposedSplitIndices,
  rowAssignments,
  repairTargetSpan,
  previouslyAppliedRowIds,
  notes,
  diagnostics,
}: {
  targetWitness: Witness
  rows: AnchoredRow[]
  selectedRowIds: string[]
  contextRowIds?: string[]
  anchorRowIds?: string[]
  proposedSplitIndices: number[]
  rowAssignments: WorkbenchPropagationRowAssignment[]
  repairTargetSpan?: {
    startChar: number
    endChar: number
  }
  previouslyAppliedRowIds?: string[]
  notes?: string
  diagnostics?: WorkbenchPropagationDiagnostics
}): SelectedUnitPropagationRepairPreview => {
  const segmentableWordSpans = getSegmentableWordSpansForWitness(targetWitness)
  const existingSplitIndices = deriveSplitIndicesFromSegmentation(targetWitness.segmentation_result, segmentableWordSpans)
  const selectedInterval = repairTargetSpan
    ? deriveTokenIntervalFromCharRange({
        segmentableWordSpans,
        startChar: repairTargetSpan.startChar,
        endChar: repairTargetSpan.endChar,
      })
    : deriveSelectedRepairTokenInterval({
        targetWitness,
        rows,
        selectedRowIds,
        segmentableWordSpans,
        existingSplitIndices,
      })
  const splitIndices = selectedInterval
    ? mergeSelectedRepairSplitIndices({
        existingSplitIndices,
        proposedSplitIndices,
        selectedStartTokenIndex: selectedInterval.startTokenIndex,
        selectedEndTokenIndex: selectedInterval.endTokenIndex,
        totalTokenCount: segmentableWordSpans.length,
      })
    : normalizeSplitIndices(proposedSplitIndices, segmentableWordSpans.length)
  const scopedRowAssignments = rowAssignments
    .map(assignment => {
      const remappedSegmentIndex = (
        typeof assignment.startTokenIndex === 'number'
        && typeof assignment.endTokenIndex === 'number'
      )
        ? findSegmentIndexForTokenRange({
            splitIndices,
            totalTokenCount: segmentableWordSpans.length,
            startTokenIndex: assignment.startTokenIndex,
            endTokenIndex: assignment.endTokenIndex,
          })
        : assignment.segmentIndex
      return {
        ...assignment,
        segmentIndex: remappedSegmentIndex,
      }
    })

  return {
    targetWitness,
    selectedRowIds: [...selectedRowIds],
    contextRowIds: Array.from(new Set(contextRowIds || [])),
    anchorRowIds: Array.from(new Set(anchorRowIds || [])),
    previouslyAppliedRowIds: Array.from(new Set(previouslyAppliedRowIds || [])),
    baseRowFingerprints: rows.reduce<Record<string, string>>((fingerprints, row) => {
      fingerprints[row.id] = JSON.stringify(row)
      return fingerprints
    }, {}),
    ...(selectedInterval ? { selectedRepairTokenInterval: selectedInterval } : {}),
    splitIndices,
    rowAssignments: scopedRowAssignments,
    segmentCount: splitIndices.length + (segmentableWordSpans.length > 0 ? 1 : 0),
    ...(notes ? { notes } : {}),
    ...(diagnostics ? { diagnostics } : {}),
  }
}

export const analyzeAppliedWitnessCoverage = ({
  rows,
  witness,
}: {
  rows: AnchoredRow[]
  witness: Witness
}): AppliedWitnessCoverageDiagnostics => {
  const sortedRows = sortRows(rows)
  const orderedUnits = getOrderedUnits(witness.segmentation_result)
  const unitLookup = buildUnitReferenceIndexWithRemap({
    units: orderedUnits,
    idRemap: witness.segmentation_result?.id_remap,
  })

  const mappedRowIds: string[] = []
  const deliberateGapRowIds: string[] = []
  const unmappedRowIds: string[] = []
  const staleMappingRowIds: string[] = []
  const rowIdsByUnitUuid = new Map<string, string[]>()

  sortedRows.forEach(row => {
    const activeMapping = getActiveMapping(row, witness.id)
    if (activeMapping) {
      const unitIndex = unitLookup.get(activeMapping.unitUuid)
      if (typeof unitIndex === 'number') {
        mappedRowIds.push(row.id)
        const rowIds = rowIdsByUnitUuid.get(activeMapping.unitUuid) || []
        rowIds.push(row.id)
        rowIdsByUnitUuid.set(activeMapping.unitUuid, rowIds)
        return
      }
      staleMappingRowIds.push(row.id)
      return
    }

    if (hasExcludedWitnessMapping(row, witness.id)) {
      deliberateGapRowIds.push(row.id)
      return
    }

    unmappedRowIds.push(row.id)
  })

  const mappedUnitUuidSet = new Set(rowIdsByUnitUuid.keys())
  const duplicateUnitMappings = Array.from(rowIdsByUnitUuid.entries())
    .filter(([, rowIds]) => rowIds.length > 1)
    .map(([unitUuid, rowIds]) => ({ unitUuid, rowIds }))
  const unclaimedUnits = orderedUnits
    .filter(unit => {
      const unitUuid = String(unit.unit_uuid || unit.unit_id || '').trim()
      return unitUuid && !mappedUnitUuidSet.has(unitUuid)
    })
    .map(unit => ({
      unitUuid: String(unit.unit_uuid || unit.unit_id || '').trim(),
      ...(unit.unit_label ? { unitLabel: unit.unit_label } : {}),
      text: unit.unit_text_content || '',
    }))

  return {
    hasSegmentationResult: Boolean(witness.segmentation_result),
    totalRows: sortedRows.length,
    totalUnits: orderedUnits.length,
    mappedRowIds,
    deliberateGapRowIds,
    unmappedRowIds,
    staleMappingRowIds,
    duplicateUnitMappings,
    unclaimedUnits,
  }
}

export const buildAppliedWitnessAuditPlan = ({
  rows,
  witness,
}: {
  rows: AnchoredRow[]
  witness: Witness
}): AppliedWitnessAuditPlan => {
  const sortedRows = sortRows(rows)
  const text = witness.uploaded_file?.content || ''
  const wordSpans = extractWordSpans(text)
  const foliationSpans = extractFoliationSpans(text, wordSpans)
  const segmentableWordSpans = getSegmentableWordData(wordSpans, foliationSpans).segmentableWordSpans
  const splitIndices = deriveSplitIndicesFromSegmentation(witness.segmentation_result, segmentableWordSpans)
  const diagnostics = analyzeAppliedWitnessCoverage({ rows: sortedRows, witness })

  return {
    splitIndices,
    segmentCount: getOrderedUnits(witness.segmentation_result).length,
    rowAssignments: buildAppliedWitnessRowAssignments({ rows: sortedRows, witness }),
    diagnostics,
    notes: summarizeAppliedWitnessCoverageDiagnostics(diagnostics),
  }
}

export const buildAppliedWitnessRowAssignments = ({
  rows,
  witness,
}: {
  rows: AnchoredRow[]
  witness: Witness
}): WorkbenchPropagationRowAssignment[] => {
  const sortedRows = sortRows(rows)
  const orderedUnits = getOrderedUnits(witness.segmentation_result)
  const unitLookup = buildUnitReferenceIndexWithRemap({
    units: orderedUnits,
    idRemap: witness.segmentation_result?.id_remap,
  })

  return sortedRows.map(row => {
    const activeMapping = getActiveMapping(row, witness.id)
    if (!activeMapping) {
      return {
        rowId: row.id,
        segmentIndex: null,
      }
    }
    const unitIndex = unitLookup.get(activeMapping.unitUuid)
    return {
      rowId: row.id,
      segmentIndex: typeof unitIndex === 'number' ? unitIndex : null,
      ...(activeMapping.translationStatus ? { translationStatus: activeMapping.translationStatus } : {}),
    }
  })
}

export const buildWorkbenchAppliedAuditPreview = ({
  sourceWitness,
  candidateWitnesses,
  rows = [],
}: {
  sourceWitness: Witness
  candidateWitnesses: Witness[]
  rows?: AnchoredRow[]
}): WorkbenchPropagationPreview => {
  const sourceText = sourceWitness.uploaded_file?.content || ''
  const sourceWordSpans = extractWordSpans(sourceText)
  const sourceFoliationSpans = extractFoliationSpans(sourceText, sourceWordSpans)
  const sourceSegmentableWordSpans = getSegmentableWordData(sourceWordSpans, sourceFoliationSpans).segmentableWordSpans
  const sourceSplitIndices = deriveSplitIndicesFromSegmentation(sourceWitness.segmentation_result, sourceSegmentableWordSpans)

  return {
    sourceWitness,
    sourceSplitIndices,
    segmentCount: rows.length,
    protectedWitnessIds: new Set(),
    mode: 'applied-audit',
    plans: candidateWitnesses
      .filter(witness => Boolean(witness.uploaded_file?.content?.trim()))
      .map(witness => {
        if (witness.id === sourceWitness.id) {
          return {
            witness,
            splitIndices: sourceSplitIndices,
            segmentCount: getOrderedUnits(witness.segmentation_result).length,
            protected: false,
            strategy: 'single-source' as const,
            notes: summarizeAppliedWitnessCoverageDiagnostics(analyzeAppliedWitnessCoverage({ rows, witness })),
          }
        }

        const auditPlan = buildAppliedWitnessAuditPlan({ rows, witness })
        return {
          witness,
          splitIndices: auditPlan.splitIndices,
          segmentCount: auditPlan.segmentCount,
          protected: false,
          rowAssignments: auditPlan.rowAssignments,
          notes: auditPlan.notes,
        }
      }),
  }
}

const applyPropagationPlanToRows = ({
  rows,
  sourceWitness,
  targetWitness,
  nextSegmentationResult,
  rowAssignments
}: {
  rows: AnchoredRow[]
  sourceWitness: Witness
  targetWitness: Witness
  nextSegmentationResult: CompleteMicroSegmentationResult
  rowAssignments?: WorkbenchPropagationRowAssignment[]
}): { rows: AnchoredRow[]; changedRowIds: string[] } => {
  const sortedRows = sortRows(rows)
  const targetUnits = getOrderedUnits(nextSegmentationResult)

  if (targetUnits.length === 0 || sortedRows.length === 0) {
    return { rows: sortedRows, changedRowIds: [] }
  }

  const changedRowIds = new Set<string>()
  const assignedSegmentIndexByRowId = new Map<string, number | null>()
  const translationStatusByRowId = new Map<string, WorkbenchPropagationRowAssignment['translationStatus']>()

  if (rowAssignments && rowAssignments.length > 0) {
    rowAssignments.forEach(assignment => {
      assignedSegmentIndexByRowId.set(assignment.rowId, assignment.segmentIndex)
      if (assignment.translationStatus) {
        translationStatusByRowId.set(assignment.rowId, assignment.translationStatus)
      }
    })
  } else {
    const sourceUnits = getOrderedUnits(sourceWitness.segmentation_result)
    if (sourceUnits.length === 0) {
      return { rows: sortedRows, changedRowIds: [] }
    }

    const sourceLookup = buildUnitReferenceIndexWithRemap({
      units: sourceUnits,
      idRemap: sourceWitness.segmentation_result?.id_remap
    })

    const sourceRows = sortedRows
      .map(row => {
        const mapping = getActiveMapping(row, sourceWitness.id)
        if (!mapping) {
          return null
        }
        const unitIndex = sourceLookup.get(mapping.unitUuid)
        if (typeof unitIndex !== 'number') {
          return null
        }
        return { row, unitIndex }
      })
      .filter((entry): entry is { row: AnchoredRow; unitIndex: number } => Boolean(entry))
      .sort((left, right) => left.unitIndex - right.unitIndex)

    if (sourceRows.length === 0) {
      return { rows: sortedRows, changedRowIds: [] }
    }

    const maxMappedCount = Math.min(sourceRows.length, targetUnits.length)
    for (let index = 0; index < maxMappedCount; index += 1) {
      const sourceRow = sourceRows[index]
      if (sourceRow) {
        assignedSegmentIndexByRowId.set(sourceRow.row.id, index)
      }
    }
  }

  const nextRows = sortedRows.map(row => {
    const targetUnitIndex = assignedSegmentIndexByRowId.get(row.id)
    const targetUnit = typeof targetUnitIndex === 'number' ? targetUnits[targetUnitIndex] : undefined
    const existingTargetMapping = getActiveMapping(row, targetWitness.id)

    if (!targetUnit) {
      if (!existingTargetMapping) {
        return row
      }
      changedRowIds.add(row.id)
      return replaceWitnessMappings(row, targetWitness.id, [])
    }

    const nextUnitUuid = String(targetUnit.unit_uuid || targetUnit.unit_id || '').trim()
    if (!nextUnitUuid) {
      return row
    }

    const nextMapping: UnitMapping = {
      witnessId: targetWitness.id,
      unitUuid: nextUnitUuid,
      unitLabel: targetUnit.unit_label || existingTargetMapping?.unitLabel,
      textSnapshot: targetUnit.unit_text_content || existingTargetMapping?.textSnapshot || '',
      confidence: typeof targetUnit.confidence_score === 'number' ? targetUnit.confidence_score : existingTargetMapping?.confidence,
      ...(translationStatusByRowId.get(row.id) ? { translationStatus: translationStatusByRowId.get(row.id) } : {}),
      addedAt: existingTargetMapping?.addedAt || new Date().toISOString()
    }

    const mappingChanged =
      !existingTargetMapping ||
      existingTargetMapping.unitUuid !== nextMapping.unitUuid ||
      existingTargetMapping.unitLabel !== nextMapping.unitLabel ||
      existingTargetMapping.textSnapshot !== nextMapping.textSnapshot ||
      existingTargetMapping.translationStatus !== nextMapping.translationStatus

    if (!mappingChanged) {
      return row
    }

    changedRowIds.add(row.id)
    return replaceWitnessMappings(row, targetWitness.id, [nextMapping])
  })

  return {
    rows: nextRows,
    changedRowIds: Array.from(changedRowIds)
  }
}

export const buildWorkbenchPropagationApplication = ({
  preview,
  contextId,
  rows,
  options
}: {
  preview: WorkbenchPropagationPreview
  contextId: string
  rows: AnchoredRow[]
  options?: WorkbenchPropagationApplyOptions
}): WorkbenchPropagationApplication => {
  const witnessUpdates = buildWorkbenchPropagationUpdates({
    preview,
    contextId,
    options
  })

  let nextRows = sortRows(rows)
  const changedRowIds = new Set<string>()

  witnessUpdates.forEach(update => {
    const targetWitness = preview.plans.find(plan => plan.witness.id === update.witnessId)?.witness
    if (!targetWitness) {
      return
    }

    const result = applyPropagationPlanToRows({
      rows: nextRows,
      sourceWitness: preview.sourceWitness,
      targetWitness,
      nextSegmentationResult: update.result,
      rowAssignments: preview.plans.find(plan => plan.witness.id === update.witnessId)?.rowAssignments
    })
    nextRows = result.rows
    result.changedRowIds.forEach(rowId => changedRowIds.add(rowId))
  })

  return {
    witnessUpdates,
    rowUpdates: nextRows.filter(row => changedRowIds.has(row.id)),
    changedRowIds: Array.from(changedRowIds)
  }
}

export const buildWitnessScopedPropagationApplication = ({
  proposal,
  contextId,
  rows
}: {
  proposal: WitnessScopedPropagationProposal
  contextId: string
  rows: AnchoredRow[]
}): WorkbenchPropagationApplication => {
  const result = buildManualSegmentationResult({
    text: proposal.targetWitness.uploaded_file?.content || '',
    witnessId: proposal.targetWitness.id,
    witnessSigla: proposal.targetWitness.sigla,
    clusterId: `workbench:${contextId}`,
    sourceGroundTruthSigla: proposal.referenceWitness.sigla,
    splitIndices: proposal.splitIndices,
    propagated: true,
    previousSegmentationResult: proposal.targetWitness.segmentation_result
  })

  const witnessUpdate = {
    witnessId: proposal.targetWitness.id,
    type: 'segmentation' as const,
    result,
    ...(proposal.targetWitness.processing_status !== 'completed'
      ? { status: 'completed' as Witness['processing_status'] }
      : {})
  }

  const applied = applyPropagationPlanToRows({
    rows,
    sourceWitness: proposal.referenceWitness,
    targetWitness: proposal.targetWitness,
    nextSegmentationResult: result,
    rowAssignments: proposal.rowAssignments
  })

  return {
    witnessUpdates: [witnessUpdate],
    rowUpdates: applied.rows.filter(row => applied.changedRowIds.includes(row.id)),
    changedRowIds: applied.changedRowIds
  }
}

export const buildSelectedUnitPropagationRepairApplication = ({
  preview,
  contextId,
  rows,
}: {
  preview: SelectedUnitPropagationRepairPreview
  contextId: string
  rows: AnchoredRow[]
}): WorkbenchPropagationApplication => {
  const segmentableWordSpans = getSegmentableWordSpansForWitness(preview.targetWitness)
  const repairCharRange = deriveTokenIntervalCharRange(
    segmentableWordSpans,
    preview.selectedRepairTokenInterval
  )
  const result = buildSelectedRepairScopedSegmentationResult({
    preview,
    contextId,
    repairCharRange,
  })
  const reconciled = reconcileLedgerSafeBoundaryEdit({
    witnessId: preview.targetWitness.id,
    previousSegmentationResult: preview.targetWitness.segmentation_result,
    nextSegmentationResult: result,
    rows,
  })
  const {
    scopedRowAssignments,
    outOfScopeAssignmentRowIds,
    outsideLocalContextAssignmentRowIds,
  } = deriveSelectedRepairScopedRowAssignments({ preview, rows })
  const resultScopedRowAssignments = remapRowAssignmentsToSegmentationResult({
    rowAssignments: scopedRowAssignments,
    result,
    segmentableWordSpans,
  })
  const previouslyAppliedRowIdSet = new Set(preview.previouslyAppliedRowIds)
  const writableRowIdSet = new Set([
    ...preview.selectedRowIds.filter(rowId => !previouslyAppliedRowIdSet.has(rowId)),
    ...resultScopedRowAssignments.map(assignment => assignment.rowId),
  ])
  const reconciledRowById = new Map(reconciled.updatedRows.map(row => [row.id, row]))
  const writableRows = sortRows(rows)
    .filter(row => writableRowIdSet.has(row.id))
    .map(row => reconciledRowById.get(row.id) || row)
  const applied = applyPropagationPlanToRows({
    rows: writableRows,
    sourceWitness: preview.targetWitness,
    targetWitness: preview.targetWitness,
    nextSegmentationResult: result,
    rowAssignments: resultScopedRowAssignments,
  })
  const appliedRowById = new Map(applied.rows.map(row => [row.id, row]))
  const rowsAfterSelectedRepair = sortRows(rows).map(row => appliedRowById.get(row.id) || row)
  const changedRowIds = Array.from(new Set([
    ...reconciled.changedRowIds.filter(rowId => writableRowIdSet.has(rowId)),
    ...applied.changedRowIds,
  ]))
  const changedRowIdSet = new Set(changedRowIds)
  const existingOutOfRepairUnassignedFragmentKeys = new Set(reconcileLedgerSafeBoundaryEdit({
    witnessId: preview.targetWitness.id,
    previousSegmentationResult: preview.targetWitness.segmentation_result,
    nextSegmentationResult: preview.targetWitness.segmentation_result,
    rows,
  }).unassignedFragments
    .filter(fragment => !overlapsSelectedRepairCharRange(fragment, repairCharRange))
    .map(buildUnassignedFragmentSpanKey))
  const unassignedFragmentUnitUuids = Array.from(new Set(reconcileLedgerSafeBoundaryEdit({
    witnessId: preview.targetWitness.id,
    previousSegmentationResult: result,
    nextSegmentationResult: result,
    rows: rowsAfterSelectedRepair,
  }).unassignedFragments
    .filter(fragment => !overlapsSelectedRepairCharRange(fragment, repairCharRange))
    .filter(fragment => !existingOutOfRepairUnassignedFragmentKeys.has(buildUnassignedFragmentSpanKey(fragment)))
    .map(fragment => fragment.unitUuid)
    .filter(Boolean)))

  return {
    witnessUpdates: [{
      witnessId: preview.targetWitness.id,
      type: 'segmentation',
      result,
      ...(preview.targetWitness.processing_status !== 'completed'
        ? { status: 'completed' as Witness['processing_status'] }
        : {}),
    }],
    rowUpdates: rowsAfterSelectedRepair.filter(row => changedRowIdSet.has(row.id)),
    changedRowIds,
    ...(unassignedFragmentUnitUuids.length > 0 ? { unassignedFragmentUnitUuids } : {}),
    ...(outOfScopeAssignmentRowIds.length > 0 ? { outOfScopeAssignmentRowIds } : {}),
    ...(outsideLocalContextAssignmentRowIds.length > 0
      ? { outsideLocalContextAssignmentRowIds }
      : {}),
    ...(scopedRowAssignments.length > 0
      ? { acceptedAssignmentRowIds: Array.from(new Set(scopedRowAssignments.map(assignment => assignment.rowId))) }
      : {}),
  }
}

export const __workbenchPropagationTestInternals = {
  DEFAULT_PROPAGATION_TUNING,
  alignSegmentsToRows,
  buildGreedyLedgerSplitIndices,
  buildLedgerConditionedPlan,
  buildPropagationRowProfiles,
  buildWitnessSplitPlan,
  computeBoundaryOutlierPenalty,
  computeTokenLcsLength,
  computeWitnessTokenSimilarity,
  countTokenOverlap,
  deriveSplitIndicesFromSegmentation,
  derivePlanSegments,
  findNearestSplitIndex,
  filterLedgerConditionedCandidateExemplars,
  getProtectedWitnessIdsFromRows,
  createPropagationTokenCache,
  getAnchorScoreBoost,
  getTokenOrEmpty,
  looseTokenSimilarity,
  mapSplitIndicesByAnchors,
  mapSplitIndicesByProportion,
  medianNumber,
  normalizePropagationToken,
  rankPropagationExemplarWitnessIds,
  scoreRowExemplar,
  scoreRowSpan,
  scoreSplitAnchorAlignment,
  tokenizePropagationText,
}
